import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SketchPicker } from 'react-color';
import axios from 'axios';
import {
  updateComponent,
  removeComponent,
  selectSelectedComponent
} from '../redux/editorSlice';
import { COMPONENT_TYPES } from '../constants';
import LayoutSelector from './LayoutSelector';
import './PropertyPanel.css';
import RichEditorModal from './RichEditorModal';

// 모든 axios 요청에 캐싱 방지 헤더 추가
axios.interceptors.request.use(function (config) {
  // 캐싱 방지 헤더 설정
  config.headers['Cache-Control'] = 'no-cache';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  return config;
}, function (error) {
  return Promise.reject(error);
});

function PropertyPanel() {
  const dispatch = useDispatch();
  const selectedComponent = useSelector(selectSelectedComponent);
  // 일단 주석 처리하고 필요시 나중에 활용
  // const allComponents = useSelector(selectComponents);
  const [activeTab, setActiveTab] = useState('layout'); // 'layout' 또는 'component'
  
  // 디바이스 탭 상태 추가 (PC/모바일)
  const [boardDeviceTab, setBoardDeviceTab] = useState(() => {
    // 초기 화면 크기에 따라 적절한 디바이스 탭 설정
    // BoardComponent와 동일한 로직으로 일관성 유지
    return window.innerWidth <= 768 ? 'mobile' : 'pc';
  });
  
  // 디바이스 속성 탭 상태 추가 (PC, 태블릿, 모바일)
  const [devicePropertyTab, setDevicePropertyTab] = useState('pc');
  
  // 패딩 입력값 관리를 위한 로컬 상태
  const [paddingInputs, setPaddingInputs] = useState({
    top: '',
    right: '',
    bottom: '',
    left: ''
  });
  
  // 테두리 둥글기 입력값 관리를 위한 로컬 상태 추가
  const [borderRadiusInput, setBorderRadiusInput] = useState('');
  
  // 디바이스별 스타일을 강제로 적용하기 위한 참조값들
  const forceApplyTimerRef = useRef(null);
  const initialRenderRef = useRef(true);
  const styleInitializedRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  
  // RICH_EDITOR 컴포넌트용 에디터 모달 상태
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  
  // 배너 편집 상태 관리
  const [showBannerEdit, setShowBannerEdit] = useState(false);
  const [bannerEditData, setBannerEditData] = useState({
    index: -1,
    title: '',
    description: '',
    url: '',
    icon: '',
    mediaType: 'image',
    videoUrl: '',
    autoplay: true,  // 항상 기본값 true
    muted: true,     // 항상 기본값 true
    loop: true,
    position: '5',    // 기본값: 가운데 중앙
    textColor: 'white', // 텍스트 색상
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)', // 텍스트 그림자
    buttonBgColor: '#ff6b6b', // 버튼 배경색
    buttonTextColor: 'white', // 버튼 텍스트색
    buttonHoverColor: '#ff5252', // 버튼 호버 색상
    buttonText: '자세히 보기', // 버튼 텍스트
    // 새로 추가된 속성들
    transparentButton: false, // 버튼 배경 투명 여부
    buttonWidth: 'auto', // 버튼 너비
    buttonHeight: 'auto', // 버튼 높이
    mobileButtonWidth: 'auto', // 모바일에서 버튼 너비
    mobileButtonHeight: 'auto' // 모바일에서 버튼 높이
  });
  
  // 포커스 상태를 추적하는 상태
  const [focusedProps, setFocusedProps] = useState({});
  
  // 모든 컴포넌트 타입별 모달 상태 추가 (조건부 Hook 호출 방지)
  // FAQ 관련 상태
  const [faqItems, setFaqItems] = useState([]);
  
  // 컴퍼니 히스토리 관련 상태
  const [historyItems, setHistoryItems] = useState([]);
  
  // ProductSlider 관련 상태
  const [productSliderProps, setProductSliderProps] = useState({});
  
  // 리치 에디터 상태 (이미지-텍스트 컴포넌트용)
  const [isRichEditorOpen, setIsRichEditorOpen] = useState(false);
  const [richEditorContent, setRichEditorContent] = useState('');
  
  // 전역 이벤트 리스너 추가 - 디바이스 타입 변경 이벤트 수신
  useEffect(() => {
    // 다른 컴포넌트에서 디바이스 타입 변경 이벤트를 받을 때 처리
    const handleDeviceTypeChanged = (event) => {
      const { deviceType } = event.detail;
      
      // 디바이스 타입에 맞게 탭 변경
      if (deviceType === 'pc') {
        setDevicePropertyTab('pc');
        
        // 해당 디바이스 타입의 속성값 로드
        if (selectedComponent) {
          loadDeviceSpecificProperties('pc');
        }
      } else if (deviceType === 'mobile' || deviceType === 'tablet') {
        setDevicePropertyTab('mobile');
        
        // 해당 디바이스 타입의 속성값 로드
        if (selectedComponent) {
          loadDeviceSpecificProperties('mobile');
        }
      }
    };
    
    // 이벤트 리스너 등록
    window.addEventListener('deviceTypeChanged', handleDeviceTypeChanged);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('deviceTypeChanged', handleDeviceTypeChanged);
    };
  }, [selectedComponent]);
  
  // 기타 필요한 상태들을 모두 여기에 추가
  
  // 글로벌 이벤트를 이용한 스타일 강제 적용
  useEffect(() => {
    // 문서가 완전히 로드된 후 스타일 강제 적용
    const onFullLoad = () => {
      if (selectedComponent && selectedComponent.type === COMPONENT_TYPES.BOARD) {
        setTimeout(() => {
          ensureDeviceStylesApplied(true); // 강제 적용 플래그 전달
        }, 100);
      }
    };
    
    window.addEventListener('load', onFullLoad);
    return () => window.removeEventListener('load', onFullLoad);
  }, [selectedComponent]);
  
  // 브라우저 리사이즈에 따라 디바이스 탭 자동 변경 - useLayoutEffect 사용
  useLayoutEffect(() => {
    const handleResize = () => {
      // BoardComponent와 동일한 로직으로 디바이스 구분
      const isMobile = window.innerWidth <= 768;
      const newDeviceTab = isMobile ? 'mobile' : 'pc';
      
      // 디바이스 탭 변경 시에만 업데이트
      if (newDeviceTab !== boardDeviceTab) {
        setBoardDeviceTab(newDeviceTab);
        
        // 디바이스 변경 시 강제로 스타일 적용 (지연 후 적용)
        if (selectedComponent && selectedComponent.type === COMPONENT_TYPES.BOARD) {
          setTimeout(() => {
            ensureDeviceStylesApplied(true);
          }, 50);
        }
      }
    };
    
    // 초기 실행 (컴포넌트 마운트 시)
    handleResize();
    
    // 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [boardDeviceTab, selectedComponent]);
  
  // PC 모드일 때 정기적으로 스타일 체크 (초기 5초 동안)
  useEffect(() => {
    if (selectedComponent?.type === COMPONENT_TYPES.BOARD) {
      // 모든 디바이스 모드에서 정기적 체크 실행
      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - mountTimeRef.current;
        
        // 마운트 후 5초 동안만 정기 체크 실행
        if (elapsed < 5000) {
          applyBoardStylesWithFallback(true);
        } else {
          clearInterval(interval);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [selectedComponent, boardDeviceTab]);
  
  // HTTP 오류와 관계없이 스타일을 적용하기 위한 함수
  const ensureDeviceStylesApplied = (forceApply = false) => {
    try {
      // 디바이스별 스타일 강제 적용
      if (selectedComponent && selectedComponent.type === COMPONENT_TYPES.BOARD) {
        applyBoardStylesWithFallback(forceApply);
        
        // 타이머 정리
        if (forceApplyTimerRef.current) {
          clearTimeout(forceApplyTimerRef.current);
        }
        
        // 적극적인 타이밍으로 다중 시도 (모든 디바이스 모드에서)
        const delays = [100, 300, 600, 1000, 1500]; 
        
        // 여러 번 시도 (여러 시점에 적용)
        let lastTimer = null;
        delays.forEach((delay, index) => {
          lastTimer = setTimeout(() => {
            applyBoardStylesWithFallback(true);
            
            // 마지막 시도 후 스타일 초기화 완료 표시
            if (index === delays.length - 1) {
              styleInitializedRef.current = true;
            }
          }, delay);
        });
        
        // 마지막 타이머 참조 저장
        forceApplyTimerRef.current = lastTimer;
      }
    } catch (error) {
      console.error('[보드 스타일] 스타일 적용 중 오류 발생:', error);
    }
  };
  
  // 안전하게 스타일을 적용하는 함수 (강제 적용 옵션 추가)
  const applyBoardStylesWithFallback = (forceApply = false) => {
    try {
      if (!selectedComponent || selectedComponent.type !== COMPONENT_TYPES.BOARD) return;
      
      // 현재 선택된 컴포넌트의 스타일 객체 (깊은 복사)
      const currentStyle = {...(selectedComponent.style || {})};
      
      // 디바이스별 스타일 초기화 (필요시)
      if (!currentStyle.device) {
        currentStyle.device = { pc: {}, mobile: {} };
        
        // 기존 스타일 속성을 디바이스별로 복사
        ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 
         'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
         'borderRadius'].forEach(key => {
          if (currentStyle[key] !== undefined) {
            currentStyle.device.pc[key] = currentStyle[key];
            currentStyle.device.mobile[key] = currentStyle[key];
          }
        });
      }
      
      // 디바이스별 스타일 데이터 가져오기 (안전하게)
      const deviceStyles = currentStyle.device && 
                          currentStyle.device[boardDeviceTab] ? 
                          {...currentStyle.device[boardDeviceTab]} : {};
      
      // 적용할 스타일 객체
      const paddingStyles = {};
      let hasChanges = false;
      
      // 패딩 스타일 적용
      const styleKeys = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 
                         'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft'];
      
      styleKeys.forEach(key => {
        if (deviceStyles[key] || forceApply) {
          // 강제 적용 모드이거나 기존 값이 있는 경우
          paddingStyles[key] = deviceStyles[key] || currentStyle[key] || '0px';
          
          // 최상위 스타일에도 동일하게 적용 (즉각적인 반영을 위해)
          currentStyle[key] = paddingStyles[key];
          hasChanges = true;
        }
      });
      
      // 테두리 둥글기 적용
      if (deviceStyles.borderRadius || forceApply) {
        paddingStyles.borderRadius = deviceStyles.borderRadius || currentStyle.borderRadius || '0px';
        currentStyle.borderRadius = paddingStyles.borderRadius;
        hasChanges = true;
      }
      
      // 모든 디바이스에서 초기 렌더링 시 항상 변경사항이 있는 것으로 처리
      if (initialRenderRef.current) {
        hasChanges = true;
        initialRenderRef.current = false;
      }
      
      // 변경사항이 있거나 강제 적용 모드인 경우 업데이트
      if (hasChanges || forceApply) {
        
        // 스타일 업데이트 - 새로운 객체 생성으로 확실한 참조 변경
        const updatedStyle = JSON.parse(JSON.stringify(currentStyle));
        
        // requestAnimationFrame 사용하여 다음 렌더링 프레임에 적용
        requestAnimationFrame(() => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              style: updatedStyle
            }
          }));
        });
      } else {
        
      }
    } catch (error) {
      console.error('[보드 스타일] 폴백 적용 중 오류:', error);
    }
  };
  
  // 선택된 컴포넌트가 변경되면 패딩 입력값 초기화 - useLayoutEffect로 변경
  useLayoutEffect(() => {
    if (selectedComponent) {
      setPaddingInputs({
        top: extractValueAndUnit(selectedComponent.style?.paddingTop || '0px').value.toString(),
        right: extractValueAndUnit(selectedComponent.style?.paddingRight || '0px').value.toString(),
        bottom: extractValueAndUnit(selectedComponent.style?.paddingBottom || '0px').value.toString(),
        left: extractValueAndUnit(selectedComponent.style?.paddingLeft || '0px').value.toString()
      });
      
      // 테두리 둥글기 입력값도 초기화
      setBorderRadiusInput(
        extractValueAndUnit(selectedComponent.style?.borderRadius || '0px').value.toString()
      );
      
      // 게시판 컴포넌트 선택 시 현재 디바이스에 맞는 스타일 즉시 적용
      if (selectedComponent.type === COMPONENT_TYPES.BOARD) {
        
        // 모든 디바이스 모드에서 스타일 강제 적용
        applyBoardStylesWithFallback(true);
        
        // 추가로 약간의 지연 후 더 강력한 적용 시도
        setTimeout(() => {
          ensureDeviceStylesApplied(true);
        }, 100);
      }
    }
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (forceApplyTimerRef.current) {
        clearTimeout(forceApplyTimerRef.current);
      }
    };
  }, [selectedComponent?.id, boardDeviceTab]);
  
  // 디바이스 탭이 변경될 때 스타일 적용 (useLayoutEffect로 변경)
  useLayoutEffect(() => {
    if (selectedComponent && selectedComponent.type === COMPONENT_TYPES.BOARD) {
      
      // 디바이스 탭 변경 시 스타일 즉시 적용 (모든 디바이스에서 동일하게)
      applyBoardStylesWithFallback(true);
      
      // 추가로 지연 적용으로 확실히 적용
      setTimeout(() => {
        ensureDeviceStylesApplied(true);
      }, 50);
    }
  }, [boardDeviceTab]);
  
  // 디바이스 속성 탭 변경 시 스타일 적용
  useEffect(() => {
    if (selectedComponent) {
      // 디바이스 속성 탭이 변경될 때마다 해당 디바이스의 스타일을 적용
      
      loadDeviceSpecificProperties(devicePropertyTab);
      
      // 약간의 지연 후 적용 (UI가 먼저 업데이트되도록)
      setTimeout(() => {
        // 현재 디바이스 타입의 속성값을 다른 컴포넌트에 알림
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('devicePropertyTabChanged', {
            detail: {
              deviceType: devicePropertyTab,
              componentId: selectedComponent.id
            }
          }));
        }
      }, 50);
    }
  }, [devicePropertyTab, selectedComponent?.id]);
  
  // 컴포넌트 마운트 시 한 번 실행 (useLayoutEffect로 변경)
  useLayoutEffect(() => {
    // 게시판 컴포넌트인 경우 스타일 확실히 적용
    if (selectedComponent && selectedComponent.type === COMPONENT_TYPES.BOARD) {
      
      // 모든 디바이스에서 강제 적용
      ensureDeviceStylesApplied(true);
      
      // 추가적인 지연 적용 (여러 시점에서 시도)
      [300, 800, 1500, 3000].forEach(delay => {
        setTimeout(() => {
          if (!styleInitializedRef.current && selectedComponent) {
            applyBoardStylesWithFallback(true);
          }
        }, delay);
      });
    }
  }, []);
  
  // 컴포넌트 선택 핸들러 - 현재 사용하지 않으므로 주석 처리
  // const handleComponentSelect = (id) => {
  //   // dispatch 선택 액션 (이미 구현되어 있는 기능)
  // };
  
  // 컴포넌트 선택 상태에 따라 활성 탭 자동 변경
  useEffect(() => {
    if (selectedComponent) {
      setActiveTab('component');
    } else {
      setActiveTab('layout');
    }
  }, [selectedComponent]);
  
  // 탭 선택 핸들러
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // 패널 상단 탭 UI
  const renderTabs = () => (
    <div className="panel-tabs">
      <button 
        className={`panel-tab ${activeTab === 'layout' ? 'active' : ''}`}
        onClick={() => handleTabChange('layout')}
      >
        레이아웃
      </button>
      <button 
        className={`panel-tab ${activeTab === 'component' ? 'active' : ''}`}
        onClick={() => handleTabChange('component')}
      >
        컴포넌트
      </button>
    </div>
  );
  
  // 디바이스별 속성 탭 UI
  const renderDevicePropertyTabs = () => (
    <div className="device-property-tabs">
      <button 
        className={`device-property-tab ${devicePropertyTab === 'pc' ? 'active' : ''}`}
        onClick={() => handleDevicePropertyTabChange('pc')}
      >
        PC
      </button>
      <button 
        className={`device-property-tab ${devicePropertyTab === 'mobile' || devicePropertyTab === 'tablet' ? 'active' : ''}`}
        onClick={() => handleDevicePropertyTabChange('mobile')}
      >
        모바일/태블릿
      </button>
    </div>
  );
  
  // 디바이스 속성 탭 변경 핸들러
  const handleDevicePropertyTabChange = (newDeviceTab) => {
    // 이전과 같은 탭을 클릭한 경우 무시
    if (devicePropertyTab === newDeviceTab) return;
    
    // 디바이스 속성 탭 상태 업데이트
    setDevicePropertyTab(newDeviceTab);
    
    // 전역 이벤트 발생 - 속성 편집기가 해당 디바이스 타입의 값을 보여주도록 함
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('propertyEditorDeviceTabChanged', {
        detail: {
          deviceType: newDeviceTab,
          componentId: selectedComponent?.id
        }
      }));
    }
    
    // 현재 선택된 컴포넌트가 있는 경우, 디바이스 속성 로드
    if (selectedComponent) {
      // PC와 모바일 속성값이 독립적으로 저장되어 있으므로
      // 탭이 변경될 때 해당 디바이스 타입의 속성값을 로드
      loadDeviceSpecificProperties(newDeviceTab);
    }
  };
  
  // 디바이스 타입에 맞는 속성값 로드 함수
  const loadDeviceSpecificProperties = (deviceType) => {
    // 선택된 컴포넌트가 없으면 리턴
    if (!selectedComponent) return;
    
    // 스타일 로드
    if (selectedComponent.style?.deviceProperty?.[deviceType]) {
      // 패딩 입력값 초기화
      const deviceStyle = selectedComponent.style.deviceProperty[deviceType];
      
      // 패딩 값 설정
      setPaddingInputs({
        top: extractValueAndUnit(deviceStyle.paddingTop || selectedComponent.style?.paddingTop || '0px').value.toString(),
        right: extractValueAndUnit(deviceStyle.paddingRight || selectedComponent.style?.paddingRight || '0px').value.toString(),
        bottom: extractValueAndUnit(deviceStyle.paddingBottom || selectedComponent.style?.paddingBottom || '0px').value.toString(),
        left: extractValueAndUnit(deviceStyle.paddingLeft || selectedComponent.style?.paddingLeft || '0px').value.toString()
      });
      
      // 테두리 둥글기 설정
      setBorderRadiusInput(
        extractValueAndUnit(deviceStyle.borderRadius || selectedComponent.style?.borderRadius || '0px').value.toString()
      );
    } else {
      // 해당 디바이스 타입의 속성이 없으면 기본 값 사용
      setPaddingInputs({
        top: extractValueAndUnit(selectedComponent.style?.paddingTop || '0px').value.toString(),
        right: extractValueAndUnit(selectedComponent.style?.paddingRight || '0px').value.toString(),
        bottom: extractValueAndUnit(selectedComponent.style?.paddingBottom || '0px').value.toString(),
        left: extractValueAndUnit(selectedComponent.style?.paddingLeft || '0px').value.toString()
      });
      
      setBorderRadiusInput(
        extractValueAndUnit(selectedComponent.style?.borderRadius || '0px').value.toString()
      );
    }
  };
  
  // 컴포넌트가 선택되면 현재 디바이스 타입에 맞는 속성값 로드
  useEffect(() => {
    if (selectedComponent) {
      loadDeviceSpecificProperties(devicePropertyTab);
    }
  }, [selectedComponent?.id]);
  
  if (!selectedComponent) {
    return (
      <div className="property-panel">
        {renderTabs()}
        
        {activeTab === 'layout' ? (
          <div className="layout-options">
            <LayoutSelector />
          </div>
        ) : (
          <div className="component-properties">
            <h3>컴포넌트 속성</h3>
            <div className="no-component-selected">
              <img src="https://via.placeholder.com/64" alt="No selection" />
              <p>편집할 컴포넌트를 선택하세요</p>
              <p>또는 왼쪽 패널에서 새 컴포넌트를 추가하세요</p>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  const handleStyleChange = (property, value) => {
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 스타일만 변경
    if (devicePropertyTab) {
      const existingStyle = JSON.parse(JSON.stringify(selectedComponent.style || {}));
      
      // 디바이스별 스타일 객체 초기화
      if (!existingStyle.deviceProperty) {
        existingStyle.deviceProperty = { pc: {}, mobile: {} };
        
        // 기존 스타일 속성을 디바이스별로 복사
        Object.keys(existingStyle).forEach(key => {
          // deviceProperty 자체는 복사하지 않음
          if (key !== 'deviceProperty' && key !== 'device') {
            existingStyle.deviceProperty.pc[key] = existingStyle[key];
            existingStyle.deviceProperty.mobile[key] = existingStyle[key];
          }
        });
      }
      
      // 현재 디바이스 속성이 없으면 초기화
      if (!existingStyle.deviceProperty[devicePropertyTab]) {
        existingStyle.deviceProperty[devicePropertyTab] = {};
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      existingStyle.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 스타일에도 적용 (즉각적인 UI 반영을 위해)
      if (devicePropertyTab === 'pc') {
        existingStyle[property] = value;
      }
      
      // 변경 내용 저장
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          style: existingStyle
        }
      }));
      
      // 현재 속성값이 변경되었음을 알리는 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('propertyChanged', {
          detail: {
            deviceType: devicePropertyTab,
            componentId: selectedComponent.id,
            property,
            value
          }
        }));
      }
    } else {
      // 기존 로직: 디바이스 속성 탭이 선택되지 않은 경우 모든 디바이스에 적용
      const existingStyle = JSON.parse(JSON.stringify(selectedComponent.style || {}));
      
      // 최상위 스타일 업데이트
      existingStyle[property] = value;
      
      // 디바이스별 속성도 같이 업데이트
      if (existingStyle.deviceProperty) {
        if (existingStyle.deviceProperty.pc) {
          existingStyle.deviceProperty.pc[property] = value;
        }
        if (existingStyle.deviceProperty.mobile) {
          existingStyle.deviceProperty.mobile[property] = value;
        }
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          style: existingStyle
        }
      }));
    }
  };
  
  const handleContentChange = (value) => {
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 컨텐츠만 변경
    if (devicePropertyTab) {
      // 깊은 복사를 위해 JSON.parse(JSON.stringify()) 사용
      const existingContent = selectedComponent.content ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        {};
      
      const isObject = typeof existingContent === 'object' && existingContent !== null;
      
      if (isObject) {
        // 객체 타입의 content인 경우
        // 디바이스별 객체 초기화
        if (!existingContent.deviceProperty) {
          existingContent.deviceProperty = {
            pc: {},
            mobile: {}
          };
          
          // 기존 속성을 PC/모바일에 복사 (deviceProperty 제외)
          Object.keys(existingContent).forEach(key => {
            if (key !== 'deviceProperty') {
              existingContent.deviceProperty.pc[key] = existingContent[key];
              existingContent.deviceProperty.mobile[key] = existingContent[key];
            }
          });
        }
        
        // 선택된 디바이스 탭에 속성 업데이트
        if (!existingContent.deviceProperty[devicePropertyTab]) {
          existingContent.deviceProperty[devicePropertyTab] = {};
        }
        
        // 객체가 아닌 경우 문자열로 처리
        if (typeof value !== 'object') {
          existingContent.deviceProperty[devicePropertyTab] = value;
        } else {
          // 객체인 경우 각 속성을 복사
          existingContent.deviceProperty[devicePropertyTab] = { ...value };
        }
        
        // PC 모드일 때만 최상위 content도 업데이트
        if (devicePropertyTab === 'pc') {
          // 문자열이면 직접 할당
          if (typeof value !== 'object') {
            existingContent.value = value;
          } 
          // 객체이면 각 속성 복사
          else if (value !== null) {
            Object.keys(value).forEach(key => {
              if (key !== 'deviceProperty') {
                existingContent[key] = value[key];
              }
            });
          }
        }
        
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: {
            content: existingContent
          }
        }));
      } else {
        // 기본 타입의 content인 경우 (문자열 등)
        const newContent = {
          value: existingContent, // 기존 값을 value 속성으로 저장
          deviceProperty: {
            pc: existingContent,
            mobile: existingContent
          }
        };
        
        // 선택된 디바이스 탭에 속성 업데이트
        newContent.deviceProperty[devicePropertyTab] = value;
        
        // PC 모드일 때만 최상위 값도 업데이트
        if (devicePropertyTab === 'pc') {
          newContent.value = value;
        }
        
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: {
            content: newContent
          }
        }));
      }
    } else {
      // 디바이스 속성 탭이 선택되지 않은 경우 모든 디바이스에 적용
      if (typeof selectedComponent.content === 'object' && selectedComponent.content !== null && selectedComponent.content.deviceProperty) {
        // 깊은 복사
        const newContent = JSON.parse(JSON.stringify(selectedComponent.content));
        
        // 최상위 값 업데이트 
        if (typeof value === 'object' && value !== null) {
          Object.keys(value).forEach(key => {
            if (key !== 'deviceProperty') {
              newContent[key] = value[key];
            }
          });
        } else {
          newContent.value = value;
        }
        
        // 디바이스별 값도 업데이트
        try {
          if (newContent.deviceProperty.pc !== undefined) {
            newContent.deviceProperty.pc = typeof value === 'object' ? { ...value } : value;
          }
          if (newContent.deviceProperty.mobile !== undefined) {
            newContent.deviceProperty.mobile = typeof value === 'object' ? { ...value } : value;
          }
        } catch (error) {
          console.error('디바이스별 속성 업데이트 오류:', error);
        }
        
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: {
            content: newContent
          }
        }));
      } else {
        // 단순 값인 경우 그대로 업데이트
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: {
            content: value
          }
        }));
      }
    }
  };
  
  const handleImageChange = (field, value) => {
    // 먼저 컴포넌트 타입에 따라 처리 방식 분리
    if (selectedComponent.type === COMPONENT_TYPES.IMAGE) {
      // 이미지 컴포넌트의 경우 content는 객체여야 함
      const existingContent = typeof selectedComponent.content === 'object' && selectedComponent.content !== null ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        { src: '', alt: '' }; // 객체가 아닌 경우 기본 객체로 초기화
      
      // 필드 업데이트
      existingContent[field] = value;
      
      // 컴포넌트 업데이트
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: existingContent
        }
      }));
    } else if (selectedComponent.type === COMPONENT_TYPES.IMAGE_TEXT) {
      // IMAGE_TEXT 타입은 componentProps를 사용하므로 handleComponentPropsChange 호출
      handleComponentPropsChange('imageUrl', value);
    } else {
      // 기타 컴포넌트 타입에 대한 기본 처리
    }
  };
  
  // 게시판, 상세 페이지 등 복잡한 컴포넌트의 데이터 변경 처리
  const handleDataChange = (field, value) => {
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 데이터만 변경
    if (devicePropertyTab) {
      // 깊은 복사를 위해 JSON.parse(JSON.stringify()) 사용
      const existingData = selectedComponent.data ? 
        JSON.parse(JSON.stringify(selectedComponent.data)) : 
        {};
      
      // 디바이스별 객체 초기화
      if (!existingData.deviceProperty) {
        existingData.deviceProperty = {
          pc: {},
          mobile: {}
        };
        
        // 기존 속성을 PC/모바일에 복사 (deviceProperty 제외)
        Object.keys(existingData).forEach(key => {
          if (key !== 'deviceProperty' && key !== 'device') {
            existingData.deviceProperty.pc[key] = existingData[key];
            existingData.deviceProperty.mobile[key] = existingData[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!existingData.deviceProperty[devicePropertyTab]) {
        existingData.deviceProperty[devicePropertyTab] = {};
      }
      
      existingData.deviceProperty[devicePropertyTab][field] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingData[field] = value;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          data: existingData
        }
      }));
    } else {
      // 깊은 복사 수행
      const existingData = selectedComponent.data ? 
        JSON.parse(JSON.stringify(selectedComponent.data)) : 
        {};
      
      // 최상위 속성 업데이트
      existingData[field] = value;
      
      // 디바이스별 속성도 업데이트
      try {
        if (existingData.deviceProperty) {
          if (existingData.deviceProperty.pc) {
            existingData.deviceProperty.pc[field] = value;
          }
          if (existingData.deviceProperty.mobile) {
            existingData.deviceProperty.mobile[field] = value;
          }
        }
      } catch (error) {
        console.error('디바이스별 속성 업데이트 오류:', error);
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          data: existingData
        }
      }));
    }
  };
  
  // 컴포넌트 속성(componentProps) 변경 핸들러 추가
  const handleComponentPropsChange = (property, value) => {
    // 깊은 복사를 위해 JSON.parse(JSON.stringify()) 사용
    const existingProps = selectedComponent.componentProps ? 
      JSON.parse(JSON.stringify(selectedComponent.componentProps)) : 
      {};

    // 디바이스별 객체 초기화
    if (!existingProps.deviceProperty) {
      existingProps.deviceProperty = {
        pc: {},
        mobile: {}
      };
      // 기존 속성을 PC/모바일에 복사 (deviceProperty 제외)
      Object.keys(existingProps).forEach(key => {
        if (key !== 'deviceProperty') {
          existingProps.deviceProperty.pc[key] = existingProps[key];
          existingProps.deviceProperty.mobile[key] = existingProps[key];
        }
      });
    }

    // 선택된 디바이스 탭에 속성 업데이트
    if (devicePropertyTab && !existingProps.deviceProperty[devicePropertyTab]) {
      existingProps.deviceProperty[devicePropertyTab] = {};
    }
    if (devicePropertyTab) {
      existingProps.deviceProperty[devicePropertyTab][property] = value;
    }
    // 항상 최상위에도 저장 (PC/모바일 관계없이)
    existingProps[property] = value;

    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  const handleDelete = () => {
    dispatch(removeComponent(selectedComponent.id));
  };
  
  // 여백 설정 관련 헬퍼 함수
  const extractValueAndUnit = (styleValue) => {
    if (!styleValue || typeof styleValue !== 'string') {
      return { value: 0, unit: 'px' };
    }
    
    // 정규식으로 값과 단위 추출 (보다 정확하게)
    const regex = /^(-?\d*\.?\d+)([a-z%]+)$/i;
    const match = styleValue.match(regex);
    
    if (match) {
      return {
        value: parseFloat(match[1]),
        unit: match[2]
      };
    } else {
      // 숫자만 있는 경우 기본 단위 px로 간주
      const numericRegex = /^(-?\d*\.?\d+)$/;
      const numericMatch = styleValue.match(numericRegex);
      
      if (numericMatch) {
        return {
          value: parseFloat(numericMatch[1]),
          unit: 'px'
        };
      }
      
      // 유효하지 않은 값인 경우 기본값 반환
      return { value: 0, unit: 'px' };
    }
  };
  
  // 특정 단위를 감지하는 함수 개선
  const detectUnitInText = (text) => {
    if (!text || typeof text !== 'string') return null;
    
    // 유효한 CSS 단위 목록 (우선순위 순으로 정렬)
    const validUnits = ['px', '%', 'em', 'rem', 'vh', 'vw', 'vmin', 'vmax', 'pt', 'pc', 'in', 'cm', 'mm', 'ex', 'ch'];
    
    // 정규식으로 단위를 포함한 값 감지 (더 정확하게)
    const unitRegex = /(-?\d*\.?\d+)([a-z%]+)$/i;
    const match = text.match(unitRegex);
    
    if (match && match[2]) {
      const detectedUnit = match[2].toLowerCase();
      if (validUnits.includes(detectedUnit)) {
        return detectedUnit;
      }
    }
    
    // 정규식으로 찾지 못한 경우 부분 문자열 검색 시도
    for (const unit of validUnits) {
      if (text.toLowerCase().endsWith(unit)) {
        return unit;
      }
    }
    
    return null;
  };
  
  // 패딩 입력을 위한 공통 핸들러 함수 (전역으로 이동)
  const handlePaddingValueChange = (paddingType, inputValue) => {
    const styleKey = `padding${paddingType}`;
    const currentStyle = selectedComponent.style?.[styleKey] || '0px';
    const { unit: currentUnit } = extractValueAndUnit(currentStyle);
    
    // 입력값에서 단위 감지
    const detectedUnit = detectUnitInText(inputValue);
    
    // 숫자만 추출 (숫자가 없으면 0 사용)
    const numericMatch = inputValue.match(/[\d.]+/);
    const numericValue = numericMatch ? parseFloat(numericMatch[0]) : 0;
    
    if (detectedUnit) {
      // 감지된 단위가 있을 경우 해당 단위 적용
      handleStyleChange(styleKey, `${numericValue}${detectedUnit}`);
    } else {
      // 단위가 감지되지 않으면 기존 단위 유지
      handleStyleChange(styleKey, `${numericValue}${currentUnit}`);
    }
    
    // 입력 필드 값을 숫자만 표시
    setPaddingInputs(prev => ({
      ...prev,
      [paddingType.toLowerCase()]: numericValue.toString()
    }));
  };
  
  // 테두리 둥글기 입력을 위한 공통 핸들러 함수
  const handleBorderRadiusChange = (inputValue) => {
    const currentStyle = selectedComponent.style?.borderRadius || '0px';
    const { unit: currentUnit } = extractValueAndUnit(currentStyle);
    
    // 입력값에서 단위 감지
    const detectedUnit = detectUnitInText(inputValue);
    
    // 숫자만 추출 (숫자가 없으면 0 사용)
    const numericMatch = inputValue.match(/[\d.]+/);
    const numericValue = numericMatch ? parseFloat(numericMatch[0]) : 0;
    
    if (detectedUnit) {
      // 감지된 단위가 있을 경우 해당 단위 적용
      handleStyleChange('borderRadius', `${numericValue}${detectedUnit}`);
    } else {
      // 단위가 감지되지 않으면 기존 단위 유지
      handleStyleChange('borderRadius', `${numericValue}${currentUnit}`);
    }
    
    // 입력 필드 값을 숫자만 표시
    setBorderRadiusInput(numericValue.toString());
  };
  
  // 일반 컴포넌트용 패딩 컨트롤 렌더링 함수
  const renderPaddingControls = () => {
    // 모든 컴포넌트에 패딩 컨트롤 렌더링
    return (
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 17H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          여백 설정
        </div>
        <div className="padding-controls">
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>상단 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={paddingInputs.top}
                onChange={(e) => {
                  // 입력값 그대로 상태에 저장 (단위 변환 처리 없음)
                  setPaddingInputs({
                    ...paddingInputs,
                    top: e.target.value
                  });
                }}
                onBlur={(e) => {
                  // 입력 완료 후에만 단위 변환 처리
                  handlePaddingValueChange('Top', e.target.value);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.paddingTop || '0px').unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>우측 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={paddingInputs.right}
                onChange={(e) => {
                  // 입력값 그대로 상태에 저장 (단위 변환 처리 없음)
                  setPaddingInputs({
                    ...paddingInputs,
                    right: e.target.value
                  });
                }}
                onBlur={(e) => {
                  // 입력 완료 후에만 단위 변환 처리
                  handlePaddingValueChange('Right', e.target.value);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.paddingRight || '0px').unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>하단 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={paddingInputs.bottom}
                onChange={(e) => {
                  // 입력값 그대로 상태에 저장 (단위 변환 처리 없음)
                  setPaddingInputs({
                    ...paddingInputs,
                    bottom: e.target.value
                  });
                }}
                onBlur={(e) => {
                  // 입력 완료 후에만 단위 변환 처리
                  handlePaddingValueChange('Bottom', e.target.value);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.paddingBottom || '0px').unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>좌측 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={paddingInputs.left}
                onChange={(e) => {
                  // 입력값 그대로 상태에 저장 (단위 변환 처리 없음)
                  setPaddingInputs({
                    ...paddingInputs,
                    left: e.target.value
                  });
                }}
                onBlur={(e) => {
                  // 입력 완료 후에만 단위 변환 처리
                  handlePaddingValueChange('Left', e.target.value);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.paddingLeft || '0px').unit}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // 컴포넌트 타입별 속성 편집 UI
  const renderProperties = () => {
    // 디바이스별 속성값을 가져오는 내부 함수 정의
    const getDevicePropertyValue = (source, field, defaultValue) => {
      if (!source) return defaultValue;
      
      try {
        // deviceProperty 객체가 있고, 현재 선택된 디바이스 탭에 해당 필드가 있는 경우
        if (source.deviceProperty && 
            source.deviceProperty[devicePropertyTab] && 
            source.deviceProperty[devicePropertyTab][field] !== undefined) {
          
          return source.deviceProperty[devicePropertyTab][field];
        }
        
        // 디바이스별 설정이 없는 경우, 최상위 필드 확인
        if (source[field] !== undefined) {
          return source[field];
        }
        
        // 모든 경우에 해당 없으면 기본값 반환
        return defaultValue;
      } catch (err) {
        console.error(`getDevicePropertyValue 오류:`, err);
        return defaultValue;
      }
    };
    
    switch(selectedComponent.type) {
      case COMPONENT_TYPES.FIXED_BG_TEXT:
        // 모바일 환경 확인
        const isMobile = devicePropertyTab === 'mobile';
        
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-image"></i> 배경 이미지 설정
              </div>
              <div className="property-control">
                <label style={{ minWidth: '30%' }}>배경 이미지</label>
                <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'bgImageUrl', 'https://via.placeholder.com/1920x1080')} 
                    onChange={(e) => handleComponentPropsChange('bgImageUrl', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <label className="upload-button" style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#4a90e2', 
                    color: 'white', 
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    업로드
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={async (e) => {
                        const imageUrl = await handleImageUpload(e);
                        if (imageUrl) {
                          handleComponentPropsChange('bgImageUrl', imageUrl);
                        }
                      }} 
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-palette"></i> 오버레이 설정
              </div>
              <div className="property-control">
                <label>오버레이 색상</label>
                <div className="color-picker-container">
                  <input 
                    type="color" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'overlayColor', '#000000')} 
                    onChange={(e) => handleComponentPropsChange('overlayColor', e.target.value)}
                  />
                </div>
              </div>
              <div className="property-control">
                <label>오버레이 불투명도</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.1" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'overlayOpacity', 0.3)} 
                  onChange={(e) => handleComponentPropsChange('overlayOpacity', parseFloat(e.target.value))}
                />
                <span>{(getDevicePropertyValue(selectedComponent.componentProps, 'overlayOpacity', 0.3) * 100).toFixed(0)}%</span>
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-font"></i> 텍스트 설정
              </div>
              <div className="property-control">
                <label>텍스트 색상</label>
                <div className="color-picker-container">
                  <input 
                    type="color" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'textColor', '#ffffff')} 
                    onChange={(e) => handleComponentPropsChange('textColor', e.target.value)}
                  />
                </div>
              </div>
              <div className="property-control">
                <label>콘텐츠 정렬</label>
                <select 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'contentAlignment', 'center')} 
                  onChange={(e) => handleComponentPropsChange('contentAlignment', e.target.value)}
                >
                  <option value="top">상단</option>
                  <option value="center">중앙</option>
                  <option value="bottom">하단</option>
                </select>
              </div>
              <div className="property-control">
                <label>텍스트 내용</label>
                <button 
                  className="edit-button"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                  onClick={() => {
                    // 현재 콘텐츠 가져오기
                    let currentContent = '';
                    if (typeof selectedComponent.content === 'object' && selectedComponent.content !== null) {
                      if (selectedComponent.content.deviceProperty && 
                          selectedComponent.content.deviceProperty[devicePropertyTab]) {
                        currentContent = selectedComponent.content.deviceProperty[devicePropertyTab];
                      } else if (selectedComponent.content.html) {
                        currentContent = selectedComponent.content.html;
                      }
                    } else if (typeof selectedComponent.content === 'string') {
                      currentContent = selectedComponent.content;
                    }
                    
                    // 리치 에디터로 편집하기 위해 상태 설정
                    setRichEditorContent(currentContent);
                    setIsRichEditorOpen(true);
                  }}
                >
                  텍스트 편집하기
                </button>
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-ruler"></i> 크기 설정
              </div>
              <div className="property-control">
                <label>높이 (px)</label>
                <input 
                  type="number" 
                  min="100" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'height', isMobile ? 400 : 500)} 
                  onChange={(e) => handleComponentPropsChange('height', parseInt(e.target.value))}
                />
              </div>
            </div>
            
            {/* 기본 스타일 설정 */}
            {renderPaddingControls()}
            {renderBorderRadiusControl()}
            {renderStyleControls()}
            
            {/* 리치 에디터 모달 */}
            {isRichEditorOpen && (
              <RichEditorModal
                isOpen={isRichEditorOpen}
                content={richEditorContent}
                initialContent={richEditorContent}
                onSave={(content) => {
                  // 디바이스별 콘텐츠 저장
                  handleRichEditorSave(content);
                  setIsRichEditorOpen(false);
                }}
                onClose={() => setIsRichEditorOpen(false)}
              />
            )}
          </>
        );
      
      case COMPONENT_TYPES.TEXT:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-font"></i> 텍스트 설정
              </div>
              <label style={{ display: 'block', marginBottom: '8px' }}>텍스트 내용</label>
              <textarea 
                value={typeof selectedComponent.content === 'object' && selectedComponent.content.deviceProperty 
                  ? getDevicePropertyValue(selectedComponent.content, devicePropertyTab, '') 
                  : selectedComponent.content} 
                onChange={(e) => handleContentChange(e.target.value)}
                style={{ width: '100%', minHeight: '120px', padding: '8px', resize: 'vertical', marginBottom: '16px' }}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>폰트 크기</label>
              <div className="input-with-unit">
                {(() => {
                  const styleValue = getDevicePropertyValue(
                    selectedComponent.style, 
                    'fontSize', 
                    selectedComponent.style.fontSize
                  );
                  const { value, unit } = extractValueAndUnit(styleValue);
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value} 
                        onChange={(e) => handleStyleChange('fontSize', `${e.target.value}${unit}`)}
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleStyleChange('fontSize', `${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                        <option value="vh">vh</option>
                        <option value="vw">vw</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>텍스트 정렬</label>
              <select 
                value={getDevicePropertyValue(selectedComponent.style, 'textAlign', 'left')} 
                onChange={(e) => handleStyleChange('textAlign', e.target.value)}
              >
                <option value="left">왼쪽</option>
                <option value="center">가운데</option>
                <option value="right">오른쪽</option>
                <option value="justify">양쪽 정렬</option>
              </select>
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>색상</label>
              <SketchPicker 
                color={getDevicePropertyValue(selectedComponent.style, 'color', '#000000')} 
                onChange={(color) => handleStyleChange('color', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>폰트 두께</label>
              <select 
                value={getDevicePropertyValue(selectedComponent.style, 'fontWeight', 'normal')} 
                onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              >
                <option value="normal">보통</option>
                <option value="bold">굵게</option>
                <option value="lighter">얇게</option>
              </select>
            </div>
            {renderPaddingControls()}
            {renderStyleControls()}
          </>
        );
        
      case COMPONENT_TYPES.IMAGE_TEXT:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-image"></i> 이미지-텍스트 설정
              </div>
              
              
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>이미지 URL</label>
                <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'imageUrl', 'https://via.placeholder.com/400x300')} 
                    onChange={(e) => handleComponentPropsChange('imageUrl', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <label className="upload-button" style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#4a90e2', 
                    color: 'white', 
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    업로드
                    <input 
                      type="file" 
                      style={{ display: 'none' }} 
                      accept="image/*"
                      onChange={async (e) => {
                        const imageUrl = await handleImageUpload(e);
                        if (imageUrl) {
                          handleComponentPropsChange('imageUrl', imageUrl);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>이미지 위치</label>
                <select
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'imagePosition', 'left')}
                  onChange={(e) => handleComponentPropsChange('imagePosition', e.target.value)}
                >
                  <option value="left">왼쪽</option>
                  <option value="right">오른쪽</option>
                </select>
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>이미지 너비</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'imageWidth', '50%')}
                  onChange={(e) => handleComponentPropsChange('imageWidth', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>텍스트 너비</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'textWidth', '50%')}
                  onChange={(e) => handleComponentPropsChange('textWidth', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>간격</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'gap', '0%')}
                  onChange={(e) => handleComponentPropsChange('gap', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>세로 정렬</label>
                <select
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'verticalAlign', 'middle')}
                  onChange={(e) => handleComponentPropsChange('verticalAlign', e.target.value)}
                >
                  <option value="top">상단</option>
                  <option value="middle">중앙</option>
                  <option value="bottom">하단</option>
                </select>
              </div>
            </div>
            
            {/* 두 번째 영역 설정: 텍스트 또는 이미지 */}
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-columns"></i> 두 번째 영역 설정
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>이미지 사용</label>
                <input 
                  type="checkbox" 
                  checked={getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false)} 
                  onChange={(e) => handleComponentPropsChange('useSecondImage', e.target.checked)}
                />
                <span style={{ marginLeft: '8px' }}>텍스트 대신 이미지 사용</span>
              </div>
              
              {getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false) && (
                <div className="property-group">
                  <label style={{ minWidth: '30%' }}>두 번째 이미지 URL</label>
                  <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
                    <input 
                      type="text" 
                      value={getDevicePropertyValue(selectedComponent.componentProps, 'secondImageUrl', 'https://via.placeholder.com/400x300')} 
                      onChange={(e) => handleComponentPropsChange('secondImageUrl', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <label className="upload-button" style={{ 
                      padding: '8px 12px', 
                      backgroundColor: '#4a90e2', 
                      color: 'white', 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      업로드
                      <input 
                        type="file" 
                        style={{ display: 'none' }} 
                        accept="image/*"
                        onChange={async (e) => {
                          try {
                            const imageUrl = await handleImageUpload(e);
                            
                            if (imageUrl) {
                              handleComponentPropsChange('secondImageUrl', imageUrl);
                                }
                          } catch (error) {
                            console.error("두 번째 이미지 업로드 오류:", error);
                            alert("두 번째 이미지 업로드 중 오류가 발생했습니다.");
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            {!getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false) && (
              <div className="property-group">
                <div className="property-group-title">
                  <i className="fas fa-font"></i> 텍스트 내용
                </div>
                {/* 리치 에디터 열기 버튼 */}
                <button 
                  onClick={() => {
                    setIsRichEditorOpen(true);
                    const content = typeof selectedComponent.content === 'object' && selectedComponent.content.deviceProperty 
                      ? getDevicePropertyValue(selectedComponent.content, devicePropertyTab, '') 
                      : selectedComponent.content || '';
                    setRichEditorContent(content);
                  }}
                  style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#4a90e2', 
                    color: 'white', 
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginBottom: '10px',
                    border: 'none',
                    width: '100%'
                  }}
                >
                  리치 에디터로 편집하기
                </button>
                
                {/* 미리보기 영역 */}
                <div 
                  className="rich-text-preview"
                  style={{ 
                    border: '1px solid #ddd', 
                    padding: '10px', 
                    minHeight: '100px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: typeof selectedComponent.content === 'object' && selectedComponent.content.deviceProperty 
                      ? getDevicePropertyValue(selectedComponent.content, devicePropertyTab, '') 
                      : selectedComponent.content || '텍스트를 편집하려면 위의 버튼을 클릭하세요.' 
                  }}
                ></div>
              </div>
            )}
            
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-paint-brush"></i> 스타일 설정
              </div>
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <SketchPicker 
                  color={getDevicePropertyValue(selectedComponent.componentProps, 'backgroundColor', 'transparent')} 
                  onChange={(color) => handleComponentPropsChange('backgroundColor', color.hex)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>내부 여백</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'padding', '20px')}
                  onChange={(e) => handleComponentPropsChange('padding', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'borderRadius', '0px')}
                  onChange={(e) => handleComponentPropsChange('borderRadius', e.target.value)}
                />
              </div>
            </div>
          </>
        );
        
      case COMPONENT_TYPES.IMAGE:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-image"></i> 이미지 설정
              </div>
              <label style={{ minWidth: '30%' }}>이미지 URL</label>
              <div className="image-url-input-container" style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                <input 
                  type="text" 
                  value={selectedComponent.content?.src || ''} 
                  onChange={(e) => handleImageChange('src', e.target.value)}
                  style={{ flex: 1 }}
                />
                <label className="upload-button" style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#4a90e2', 
                  color: 'white', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  업로드
                  <input 
                    type="file" 
                    style={{ display: 'none' }} 
                    accept="image/*"
                    onChange={async (e) => {
                      const imageUrl = await handleImageUpload(e);
                      if (imageUrl) {
                        handleImageChange('src', imageUrl);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            
            {/* 이미지 미리보기 추가 */}
            {selectedComponent.content?.src && (
              <div className="property-group">
                <div className="image-preview" style={{ 
                  marginTop: '10px', 
                  textAlign: 'center',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  padding: '8px',
                  marginBottom: '16px'
                }}>
                  <img 
                    src={selectedComponent.content.src} 
                    alt={selectedComponent.content.alt || '이미지 미리보기'} 
                    style={{ maxWidth: '100%', maxHeight: '150px' }}
                  />
                </div>
              </div>
            )}
            
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>대체 텍스트</label>
              <input 
                type="text" 
                value={selectedComponent.content?.alt || ''} 
                onChange={(e) => handleImageChange('alt', e.target.value)}
              />
            </div>
            
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
              <div className="input-with-unit">
                {(() => {
                  const { value, unit } = extractValueAndUnit(selectedComponent.style?.borderRadius || '0px');
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value} 
                        onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}${unit}`)}
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleStyleChange('borderRadius', `${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
            {renderPaddingControls()}
            {renderStyleControls()}
          </>
        );
        
      case COMPONENT_TYPES.CONTAINER:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>배경색</label>
              <SketchPicker 
                color={selectedComponent.style.backgroundColor || '#ffffff'} 
                onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>테두리</label>
              <input 
                type="text" 
                value={selectedComponent.style.border || '1px solid #000'} 
                onChange={(e) => handleStyleChange('border', e.target.value)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
              <div className="input-with-unit">
                {(() => {
                  const { value, unit } = extractValueAndUnit(selectedComponent.style.borderRadius);
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value} 
                        onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}${unit}`)}
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleStyleChange('borderRadius', `${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        );
        
      case COMPONENT_TYPES.BUTTON:
        return (
          <>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>버튼 텍스트</label>
              <input 
                type="text" 
                value={selectedComponent.content} 
                onChange={(e) => handleContentChange(e.target.value)}
              />
            </div>
            {renderPaddingControls()}
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>배경색</label>
              <SketchPicker 
                color={selectedComponent.style.backgroundColor || '#007bff'} 
                onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>텍스트 색상</label>
              <SketchPicker 
                color={selectedComponent.style.color || '#ffffff'} 
                onChange={(color) => handleStyleChange('color', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
              <div className="input-with-unit">
                {(() => {
                  const { value, unit } = extractValueAndUnit(selectedComponent.style.borderRadius);
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value} 
                        onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}${unit}`)}
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleStyleChange('borderRadius', `${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        );
        
      case COMPONENT_TYPES.LOGIN:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>배경색</label>
              <SketchPicker 
                color={selectedComponent.style.backgroundColor || '#f8f9fa'} 
                onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
              <div className="input-with-unit">
                {(() => {
                  const { value, unit } = extractValueAndUnit(selectedComponent.style.borderRadius);
                  return (
                    <>
                      <input 
                        type="number" 
                        value={value} 
                        onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}${unit}`)}
                      />
                      <select 
                        value={unit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          handleStyleChange('borderRadius', `${value}${newUnit}`);
                        }}
                      >
                        <option value="px">px</option>
                        <option value="%">%</option>
                        <option value="em">em</option>
                        <option value="rem">rem</option>
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>제목 텍스트</label>
              <input 
                type="text" 
                value={selectedComponent.data?.title || '로그인'} 
                onChange={(e) => handleDataChange('title', e.target.value)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>API URL</label>
              <input 
                type="text" 
                value={selectedComponent.data?.apiUrl || 'https://api.example.com/login'} 
                onChange={(e) => handleDataChange('apiUrl', e.target.value)}
              />
              <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                로그인 요청을 보낼 API 엔드포인트
              </small>
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>버튼 색상</label>
              <SketchPicker 
                color={selectedComponent.style.buttonColor || '#4a90e2'} 
                onChange={(color) => handleStyleChange('buttonColor', color.hex)}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>배경 타입</label>
              <select
                value={selectedComponent.componentProps?.backgroundType || 'image'}
                onChange={e => handleComponentPropsChange('backgroundType', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="image">이미지</option>
                <option value="video">비디오</option>
              </select>
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>배경 URL</label>
              <input
                type="text"
                value={selectedComponent.componentProps?.backgroundUrl || ''}
                onChange={e => handleComponentPropsChange('backgroundUrl', e.target.value)}
                placeholder="https://example.com/bg.jpg 또는 .mp4"
                style={{ width: '100%' }}
              />
            </div>
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>로그인 후 이동 경로</label>
              <input
                type="text"
                value={selectedComponent.componentProps?.redirectPath || ''}
                onChange={e => handleComponentPropsChange('redirectPath', e.target.value)}
                placeholder="예: /dashboard"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                이미 로그인된 경우 또는 로그인 성공 시 이동할 경로를 지정하세요.
              </small>
            </div>
          </>
        );
        
      case COMPONENT_TYPES.BOARD:
        // 디바이스별 데이터 변경 핸들러
        const handleBoardDeviceDataChange = (field, value) => {
          // 전체 데이터의 깊은 복사
          const existingData = JSON.parse(JSON.stringify(selectedComponent.data || {}));
          
          // 초기 디바이스 객체 설정
          if (!existingData.device) {
            existingData.device = { pc: {}, mobile: {} };
            
            // 기존 값이 있으면 초기 마이그레이션 (양쪽에 같은 값 설정)
            Object.keys(existingData).forEach(key => {
              // device 키와 업데이트할 필드 자체는 복사하지 않음
              if (key !== 'device' && key !== field) {
                existingData.device.pc[key] = existingData[key];
                existingData.device.mobile[key] = existingData[key];
              }
            });
          }
          
          // 현재 디바이스 객체 확인
          if (!existingData.device[boardDeviceTab]) {
            existingData.device[boardDeviceTab] = {};
          }
          
          // 현재 디바이스 데이터 업데이트 (다른 디바이스 데이터는 그대로 유지)
          existingData.device[boardDeviceTab][field] = value;
          
          // 최상위 필드 정리 (device 구조로 이동 완료된 필드 삭제)
          Object.keys(existingData).forEach(key => {
            // 'device' 키가 아닌 다른 모든 키 삭제
            if (key !== 'device') {
              delete existingData[key];
            }
          });
          
          // Redux 액션으로 업데이트
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              data: existingData
            }
          }));
        };
        
        // 디바이스별 값 가져오기 함수
        const getDeviceValue = (data, field, defaultValue) => {
          // 데이터가 없으면 기본값 반환
          if (!data) {
            return defaultValue;
          }
          
          try {
            // device 객체가 있고, 현재 선택된 디바이스에 해당 필드가 있는 경우
            if (data.device && 
                data.device[boardDeviceTab] && 
                data.device[boardDeviceTab][field] !== undefined) {
              
              const value = data.device[boardDeviceTab][field];
              return value;
            }
            
            // 디바이스별 설정이 없는 경우, 최상위 필드 확인
            if (data[field] !== undefined) {
              const value = data[field];
              return value;
            }
            
            // 모든 경우에 해당 없으면 기본값 반환
            return defaultValue;
          } catch (err) {
            console.error(`getDeviceValue 오류:`, err);
            return defaultValue;
          }
        };
        
        return (
          <>
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                게시판 설정
              </div>
              
              <div className="property-control">
                <label style={{ minWidth: '30%' }}>게시판 제목</label>
                <input
                  type="text"
                  value={getDeviceValue(selectedComponent.data, 'title', '')}
                  onChange={(e) => handleBoardDeviceDataChange('title', e.target.value)}
                />
              </div>
              
              <div className="property-control">
                <label style={{ minWidth: '30%' }}>API 파라미터</label>
                <input
                  type="text"
                  value={getDeviceValue(selectedComponent.data, 'parameter', '')}
                  onChange={(e) => handleBoardDeviceDataChange('parameter', e.target.value)}
                  placeholder="API 파라미터를 입력하세요"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  예: board1, board2, board3, board4 등
                </small>
              </div>
            </div>
            
            {renderBorderRadiusControl()}
            {renderPaddingControls()}
            {renderStyleControls()}
          </>
        );
      
      case COMPONENT_TYPES.DETAIL_PAGE:
        return (
          <div className="property-group">
            <div className="property-control">
              <label style={{ minWidth: '30%' }}>페이지 제목</label>
              <input
                type="text"
                value={selectedComponent.data?.title || ''}
                onChange={(e) => handleDataChange('title', e.target.value)}
              />
            </div>
            
            <div className="property-control">
              <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
              <div className="unit-input-container">
                <input 
                  type="text" 
                  className="unit-input"
                  value={borderRadiusInput}
                  onChange={(e) => {
                    // 입력값 그대로 상태에 저장 (단위 변환 처리 없음)
                    setBorderRadiusInput(e.target.value);
                  }}
                  onBlur={(e) => {
                    // 입력 완료 후에만 단위 변환 처리
                    handleBorderRadiusChange(e.target.value);
                  }}
                />
                <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.borderRadius || '0px').unit}</span>
              </div>
            </div>
            
            {renderPaddingControls()}
          </div>
        );
        
      case COMPONENT_TYPES.MAIN_BANNER:
        // 배너 데이터 가져오기
        const currentBanners = selectedComponent.componentProps?.banners || [];

        return (
          <>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                배너 설정
              </div>
              
              {/* 디바이스별 배너 데이터 선택 UI */}
              <div className="device-specific-notice" style={{
                padding: '8px',
                marginBottom: '10px',
                backgroundColor: '#f0f9ff',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#0369a1'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {devicePropertyTab === 'pc' ? 'PC용 배너' : '모바일/태블릿용 배너'} 설정 중
                </div>
                <div>
                  현재 {devicePropertyTab === 'pc' ? 'PC' : '모바일/태블릿'} 환경에서 보여지는 배너를 설정하고 있습니다.
                  다른 디바이스 환경의 배너를 설정하려면 상단의 디바이스 탭을 변경하세요.
                </div>
              </div>
              
              <div className="property-control">
                <label>배너 모드</label>
                <select
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'mode', 'slider')}
                  onChange={(e) => handleComponentPropsChange('mode', e.target.value)}
                >
                  <option value="slider">슬라이더 (자동 전환)</option>
                  <option value="list">목록 (가로 스크롤)</option>
                </select>
              </div>
              
              {/* 배너 목록 관리 */}
              <div className="banner-list-management">
                <h4>배너 목록</h4>
                
                {/* 현재 디바이스 탭에 따라 배너 목록 표시 */}
                {(() => {
                  // 디바이스별 배너 가져오기
                  let banners = [];
                  if (devicePropertyTab && 
                      selectedComponent.componentProps?.deviceProperty && 
                      selectedComponent.componentProps.deviceProperty[devicePropertyTab]?.banners) {
                    banners = selectedComponent.componentProps.deviceProperty[devicePropertyTab].banners;
                  } else if (selectedComponent.componentProps?.banners) {
                    banners = selectedComponent.componentProps.banners;
                  }
                  
                  return banners.map((banner, index) => (
                    <div key={index} className="banner-item" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px',
                      marginBottom: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: banner.mediaType === 'video' ? '#f0f9ff' : '#f9fafb'
                    }}>
                      <div className="banner-preview" style={{ display: 'flex', alignItems: 'center' }}>
                        <div className="banner-thumbnail" style={{ marginRight: '10px' }}>
                          {banner.mediaType === 'video' ? (
                            <div style={{ 
                              width: '40px', 
                              height: '40px', 
                              backgroundColor: '#0ea5e9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white'
                            }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 5h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" />
                                <path d="M10 9l5 3-5 3V9z" fill="white" />
                              </svg>
                            </div>
                          ) : (
                            <img 
                              src={banner.icon || 'https://via.placeholder.com/40'} 
                              alt={banner.text || `배너 ${index + 1}`} 
                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} 
                            />
                          )}
                        </div>
                        <div className="banner-info">
                          <div style={{ fontWeight: 'bold' }}>{banner.text || `배너 ${index + 1}`}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {banner.mediaType === 'video' ? '비디오 배너' : '이미지 배너'}
                          </div>
                        </div>
                      </div>
                      <div className="banner-actions">
                        <button 
                          onClick={() => {
                            setBannerEditData({
                              index,
                              title: banner.text || '',
                              description: banner.description || '',
                              url: banner.url || '',
                              icon: banner.icon || '',
                              mediaType: banner.mediaType || 'image',
                              videoUrl: banner.videoUrl || '',
                              autoplay: banner.autoplay !== false,
                              muted: banner.muted !== false,
                              loop: banner.loop !== false,
                              position: banner.position || '5',
                              hasBackground: banner.hasBackground || false,
                              textColor: banner.textColor || 'white',
                              textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)',
                              buttonBgColor: banner.buttonBgColor || '#ff6b6b',
                              buttonTextColor: banner.buttonTextColor || 'white',
                              buttonHoverColor: banner.buttonHoverColor || '#ff5252',
                              buttonText: banner.buttonText || '자세히 보기'
                            });
                            setShowBannerEdit(true);
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#4b5563',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '4px',
                            fontSize: '12px'
                          }}
                        >
                          편집
                        </button>
                        <button 
                          onClick={() => {
                            // 삭제 확인
                            if (window.confirm(`정말로 배너 #${index + 1}을(를) 삭제하시겠습니까?`)) {
                              // 현재 디바이스 탭의 배너만 수정
                              const existingProps = selectedComponent.componentProps ? 
                                JSON.parse(JSON.stringify(selectedComponent.componentProps)) : {};
                              
                              if (devicePropertyTab && existingProps.deviceProperty) {
                                // 디바이스별 배너 삭제
                                const deviceBanners = existingProps.deviceProperty[devicePropertyTab]?.banners || [];
                                const updatedBanners = deviceBanners.filter((_, i) => i !== index);
                                
                                if (!existingProps.deviceProperty[devicePropertyTab]) {
                                  existingProps.deviceProperty[devicePropertyTab] = {};
                                }
                                existingProps.deviceProperty[devicePropertyTab].banners = updatedBanners;
                                
                                // PC 모드일 경우 최상위 배너 배열도 업데이트
                                if (devicePropertyTab === 'pc') {
                                  existingProps.banners = updatedBanners;
                                }
                              } else {
                                // 모든 디바이스 배너 삭제
                                const banners = existingProps.banners || [];
                                const updatedBanners = banners.filter((_, i) => i !== index);
                                existingProps.banners = updatedBanners;
                                
                                // deviceProperty가 있으면 모든 디바이스에 적용
                                if (existingProps.deviceProperty) {
                                  if (existingProps.deviceProperty.pc) {
                                    existingProps.deviceProperty.pc.banners = updatedBanners;
                                  }
                                  if (existingProps.deviceProperty.mobile) {
                                    existingProps.deviceProperty.mobile.banners = updatedBanners;
                                  }
                                }
                              }
                              
                              // 컴포넌트 업데이트
                              dispatch(updateComponent({
                                id: selectedComponent.id,
                                changes: {
                                  componentProps: existingProps
                                }
                              }));
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ));
                })()}
                
                {/* 새 배너 추가 버튼 */}
                <button 
                  onClick={() => {
                    setBannerEditData({
                      index: -1,
                      title: '',
                      description: '',
                      url: '#',
                      icon: '',
                      mediaType: 'image',
                      videoUrl: '',
                      autoplay: true,
                      muted: true,
                      loop: true,
                      position: '5',
                      hasBackground: false,
                      textColor: 'white',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
                      buttonBgColor: '#ff6b6b',
                      buttonTextColor: 'white',
                      buttonHoverColor: '#ff5252',
                      buttonText: '자세히 보기'
                    });
                    setShowBannerEdit(true);
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#0ea5e9',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  새 배너 추가
                </button>
              </div>
            </div>
            
            {/* 배너 편집 모달 */}
            {showBannerEdit && (
              <div className="banner-edit-modal" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  width: '90%',
                  maxWidth: '600px',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <div className="modal-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{ margin: 0 }}>
                      {bannerEditData.index >= 0 ? '배너 편집' : '새 배너 추가'}
                    </h3>
                    <button 
                      onClick={() => setShowBannerEdit(false)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        padding: '0',
                        marginLeft: 'auto'
                      }}
                    >
                      &times;
                    </button>
                  </div>
                  
                  <div className="modal-body">
                    {/* 미디어 타입 선택 UI */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>미디어 타입</label>
                      <div className="media-type-selector" style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          onClick={() => setBannerEditData({...bannerEditData, mediaType: 'image'})}
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: bannerEditData.mediaType === 'image' ? '2px solid #0ea5e9' : '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: bannerEditData.mediaType === 'image' ? '#f0f9ff' : 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>이미지</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>정적 이미지 배너</div>
                        </button>
                        <button 
                          onClick={() => setBannerEditData({...bannerEditData, mediaType: 'video'})}
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: bannerEditData.mediaType === 'video' ? '2px solid #0ea5e9' : '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: bannerEditData.mediaType === 'video' ? '#f0f9ff' : 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>비디오</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>동영상 배너</div>
                        </button>
                      </div>
                    </div>
                    
                    {/* 미디어 타입에 따라 다른 UI 표시 */}
                    {bannerEditData.mediaType === 'image' ? (
                      <div className="form-group" style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 이미지</label>
                        <div className="image-url-container" style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          marginBottom: '8px' 
                        }}>
                          <input 
                            type="text" 
                            value={bannerEditData.icon}
                            onChange={(e) => setBannerEditData({...bannerEditData, icon: e.target.value})}
                            placeholder="이미지 URL을 입력하세요"
                            style={{
                              flex: 1,
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                          <label style={{
                            padding: '8px 12px',
                            backgroundColor: '#4b5563',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}>
                            업로드
                            <input 
                              type="file" 
                              accept="image/*" 
                              onChange={handleBannerImageUpload}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                        {bannerEditData.icon && (
                          <div className="image-preview" style={{ 
                            marginTop: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px',
                            backgroundColor: '#f9fafb'
                          }}>
                            <img 
                              src={bannerEditData.icon} 
                              alt="배너 이미지 미리보기" 
                              style={{ 
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'contain'
                              }} 
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="form-group" style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>비디오 URL</label>
                        <div className="video-url-container" style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          marginBottom: '8px' 
                        }}>
                          <input 
                            type="text" 
                            value={bannerEditData.videoUrl}
                            onChange={(e) => setBannerEditData({...bannerEditData, videoUrl: e.target.value})}
                            placeholder="비디오 URL을 입력하세요"
                            style={{
                              flex: 1,
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                          <label style={{
                            padding: '8px 12px',
                            backgroundColor: '#4b5563',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}>
                            업로드
                            <input 
                              type="file" 
                              accept="video/*" 
                              onChange={handleBannerVideoUpload}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                        
                        {/* 비디오 설정 UI */}
                        <div className="video-settings" style={{ 
                          marginTop: '8px',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f9fafb'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>비디오 설정</div>
                          
                          <div className="video-option" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px' 
                          }}>
                            <input 
                              type="checkbox"
                              id="loopVideo"
                              checked={bannerEditData.loop}
                              onChange={(e) => setBannerEditData({...bannerEditData, loop: e.target.checked})}
                              style={{ marginRight: '8px' }}
                            />
                            <label htmlFor="loopVideo">반복 재생</label>
                          </div>
                          
                          <div className="video-settings-notice" style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            backgroundColor: '#f0f9ff',
                            padding: '8px',
                            borderRadius: '4px'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>참고 사항:</div>
                            <ul style={{ paddingLeft: '16px', margin: '0' }}>
                              <li>비디오는 항상 자동으로 재생됩니다.</li>
                              <li>브라우저 정책에 따라 항상 음소거 상태로 재생됩니다.</li>
                              <li>모바일 기기에서는 화면 터치없이 자동 재생을 보장하기 위해 음소거가 필수입니다.</li>
                              <li>반복 재생을 해제하면 비디오가 끝난 후 다음 배너로 자동 전환됩니다.</li>
                              <li style={{ color: '#e11d48', fontWeight: 'bold' }}>컨트롤 UI는 표시되지 않으며 배너를 클릭해도 링크로 이동하지 않습니다.</li>
                            </ul>
                          </div>
                        </div>
                        
                        {/* 썸네일 설정 */}
                        <div className="form-group" style={{ marginTop: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>비디오 썸네일 이미지</label>
                          <div className="image-url-container" style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            marginBottom: '8px' 
                          }}>
                            <input 
                              type="text" 
                              value={bannerEditData.icon}
                              onChange={(e) => setBannerEditData({...bannerEditData, icon: e.target.value})}
                              placeholder="썸네일 이미지 URL을 입력하세요"
                              style={{
                                flex: 1,
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                            />
                            <label style={{
                              padding: '8px 12px',
                              backgroundColor: '#4b5563',
                              color: 'white',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}>
                              업로드
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleBannerImageUpload}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                          {bannerEditData.icon && (
                            <div className="image-preview" style={{ 
                              marginTop: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '8px',
                              backgroundColor: '#f9fafb'
                            }}>
                              <img 
                                src={bannerEditData.icon} 
                                alt="썸네일 이미지 미리보기" 
                                style={{ 
                                  width: '100%',
                                  maxHeight: '120px',
                                  objectFit: 'contain'
                                }} 
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* 공통 배너 속성 */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 제목</label>
                      <input 
                        type="text" 
                        value={bannerEditData.title}
                        onChange={(e) => setBannerEditData({...bannerEditData, title: e.target.value})}
                        placeholder="배너 제목을 입력하세요"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 설명</label>
                      <textarea 
                        value={bannerEditData.description}
                        onChange={(e) => setBannerEditData({...bannerEditData, description: e.target.value})}
                        placeholder="배너 설명을 입력하세요"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          minHeight: '80px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 링크</label>
                      <input 
                        type="text" 
                        value={bannerEditData.url}
                        onChange={(e) => setBannerEditData({...bannerEditData, url: e.target.value})}
                        placeholder="배너 클릭 시 이동할 URL"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                    
                    {/* 배너 내용 위치 설정 그리드 UI */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>배너 내용 위치</label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '8px',
                        marginTop: '10px'
                      }}>
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(pos => (
                          <div 
                            key={pos}
                            onClick={() => setBannerEditData({...bannerEditData, position: pos})}
                            style={{
                              width: '100%',
                              aspectRatio: '1/1',
                              border: `2px solid ${bannerEditData.position === pos ? '#0ea5e9' : '#ddd'}`,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              backgroundColor: bannerEditData.position === pos ? '#f0f9ff' : 'white',
                            }}
                          >
                            {getPosLabel(pos)}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* 배경 활성화 옵션 */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        backgroundColor: '#f8fafc',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            fontWeight: 'bold', 
                            marginBottom: '4px' 
                          }}>
                            배경 활성화
                          </label>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            배너 내용에 반투명 배경을 추가합니다
                          </span>
                        </div>
                        <label style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          width: '44px',
                          height: '24px'
                        }}>
                          <input
                            type="checkbox"
                            checked={bannerEditData.hasBackground}
                            onChange={(e) => setBannerEditData({...bannerEditData, hasBackground: e.target.checked})}
                            style={{ 
                              opacity: 0,
                              width: 0,
                              height: 0
                            }}
                          />
                          <span style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: bannerEditData.hasBackground ? '#0ea5e9' : '#cbd5e1',
                            transition: '.3s',
                            borderRadius: '24px',
                            '&:before': {
                              position: 'absolute',
                              content: '""',
                              height: '16px',
                              width: '16px',
                              left: bannerEditData.hasBackground ? '24px' : '4px',
                              bottom: '4px',
                              backgroundColor: 'white',
                              transition: '.3s',
                              borderRadius: '50%'
                            }
                          }}>
                            <span style={{
                              position: 'absolute',
                              content: '""',
                              height: '16px',
                              width: '16px',
                              left: bannerEditData.hasBackground ? '24px' : '4px',
                              bottom: '4px',
                              backgroundColor: 'white',
                              transition: '.3s',
                              borderRadius: '50%'
                            }} />
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* 텍스트 색상 및 스타일 섹션 */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <div style={{
                        backgroundColor: '#f8fafc',
                        padding: '16px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h4 style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          marginTop: '0', 
                          marginBottom: '16px' 
                        }}>
                          텍스트 스타일 설정
                        </h4>
                        
                        {/* 텍스트 색상 */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            텍스트 색상
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '4px',
                              backgroundColor: bannerEditData.textColor,
                              border: '1px solid #e2e8f0'
                            }} />
                            <input
                              type="color"
                              value={bannerEditData.textColor}
                              onChange={(e) => setBannerEditData({...bannerEditData, textColor: e.target.value})}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 버튼 스타일 섹션 */}
                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <div style={{
                        backgroundColor: '#f8fafc',
                        padding: '16px',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h4 style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          marginTop: '0', 
                          marginBottom: '16px' 
                        }}>
                          버튼 스타일 설정
                        </h4>
                        
                        {/* 버튼 텍스트 */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            버튼 텍스트
                          </label>
                          <input
                            type="text"
                            value={bannerEditData.buttonText}
                            onChange={(e) => setBannerEditData({...bannerEditData, buttonText: e.target.value})}
                            placeholder="버튼에 표시할 텍스트"
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0'
                            }}
                          />
                        </div>
                        
                        {/* 투명 배경 설정 */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            backgroundColor: '#f1f5f9',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div>
                              <label style={{ 
                                display: 'block', 
                                fontWeight: 'bold', 
                                marginBottom: '4px' 
                              }}>
                                투명 배경
                              </label>
                              <span style={{ fontSize: '13px', color: '#64748b' }}>
                                버튼 배경을 투명하게 만들고 테두리를 추가합니다
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={bannerEditData.transparentButton}
                              onChange={(e) => setBannerEditData({...bannerEditData, transparentButton: e.target.checked})}
                            />
                          </div>
                        </div>
                        
                        {/* 버튼 크기 설정 */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            버튼 크기 설정 (PC)
                          </label>
                          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>너비</label>
                              <input
                                type="text"
                                value={bannerEditData.buttonWidth}
                                onChange={(e) => setBannerEditData({...bannerEditData, buttonWidth: e.target.value})}
                                placeholder="auto, 100px, 50% 등"
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>높이</label>
                              <input
                                type="text"
                                value={bannerEditData.buttonHeight}
                                onChange={(e) => setBannerEditData({...bannerEditData, buttonHeight: e.target.value})}
                                placeholder="auto, 40px 등"
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '13px'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* 버튼 미리보기 */}
                        <div style={{ 
                          marginBottom: '16px', 
                          display: 'flex',
                          justifyContent: 'center' 
                        }}>
                          <div style={{
                            backgroundColor: bannerEditData.transparentButton ? 'transparent' : bannerEditData.buttonBgColor,
                            color: bannerEditData.buttonTextColor,
                            padding: '10px 20px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            boxShadow: bannerEditData.transparentButton ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                            border: bannerEditData.transparentButton ? `1px solid ${bannerEditData.buttonTextColor}` : 'none',
                            width: bannerEditData.buttonWidth !== 'auto' ? bannerEditData.buttonWidth : 'auto',
                            height: bannerEditData.buttonHeight !== 'auto' ? bannerEditData.buttonHeight : 'auto'
                          }}>
                            {bannerEditData.buttonText || '자세히 보기'} 
                          </div>
                        </div>
                        
                        {/* 버튼 배경색 */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            버튼 배경색
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '4px',
                              backgroundColor: bannerEditData.buttonBgColor,
                              border: '1px solid #e2e8f0'
                            }} />
                            <input
                              type="color"
                              value={bannerEditData.buttonBgColor}
                              onChange={(e) => setBannerEditData({...bannerEditData, buttonBgColor: e.target.value})}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>
                        
                        {/* 버튼 텍스트 색상 */}
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            버튼 텍스트 색상
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '4px',
                              backgroundColor: bannerEditData.buttonTextColor,
                              border: '1px solid #e2e8f0'
                            }} />
                            <input
                              type="color"
                              value={bannerEditData.buttonTextColor}
                              onChange={(e) => setBannerEditData({...bannerEditData, buttonTextColor: e.target.value})}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>
                        
                        {/* 버튼 호버 색상 */}
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            호버 시 배경색
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '4px',
                              backgroundColor: bannerEditData.buttonHoverColor,
                              border: '1px solid #e2e8f0'
                            }} />
                            <input
                              type="color"
                              value={bannerEditData.buttonHoverColor}
                              onChange={(e) => setBannerEditData({...bannerEditData, buttonHoverColor: e.target.value})}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="modal-footer" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '16px'
                  }}>
                    <button 
                      onClick={() => setShowBannerEdit(false)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      취소
                    </button>
                    <button 
                      onClick={handleSaveBanner}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#0ea5e9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      저장
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {renderPaddingControls()}
            {renderBorderRadiusControl()}
            {renderStyleControls()}
          </>
        );
      
      case COMPONENT_TYPES.ROW:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 8H21" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 16H21" stroke="currentColor" strokeWidth="2" />
                </svg>
                행 속성
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>전체 너비</label>
                <div className="input-with-unit">
                  {(() => {
                    const { value, unit } = extractValueAndUnit(selectedComponent.data?.width || '100%');
                    return (
                      <>
                        <input 
                          type="number" 
                          value={value}
                          onChange={(e) => {
                            const newValue = e.target.value.trim();
                            handleDataChange('width', `${newValue}${unit}`);
                          }}
                        />
                        <select 
                          value={unit}
                          onChange={(e) => {
                            const newUnit = e.target.value;
                            handleDataChange('width', `${value}${newUnit}`);
                          }}
                        >
                          <option value="%">%</option>
                          <option value="px">px</option>
                          <option value="rem">rem</option>
                          <option value="em">em</option>
                          <option value="vh">vh</option>
                          <option value="vw">vw</option>
                        </select>
                      </>
                    );
                  })()}
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>간격 (Gutter)</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    value={selectedComponent.data?.gutter || 0} 
                    min="0"
                    max="100"
                    onChange={(e) => handleDataChange('gutter', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>가로 정렬</label>
                <select 
                  value={selectedComponent.data?.horizontalAlign || 'left'} 
                  onChange={(e) => handleDataChange('horizontalAlign', e.target.value)}
                >
                  <option value="left">왼쪽</option>
                  <option value="center">중앙</option>
                  <option value="right">오른쪽</option>
                  <option value="between">양쪽 정렬</option>
                  <option value="around">균등 간격</option>
                </select>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>세로 정렬</label>
                <select 
                  value={selectedComponent.data?.verticalAlign || 'top'} 
                  onChange={(e) => handleDataChange('verticalAlign', e.target.value)}
                >
                  <option value="top">상단</option>
                  <option value="middle">중앙</option>
                  <option value="bottom">하단</option>
                  <option value="stretch">늘이기</option>
                </select>
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 3V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 3V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                배경 스타일
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <SketchPicker 
                  color={selectedComponent.style.backgroundColor || 'rgba(247, 250, 252, 0.5)'} 
                  onChange={(color) => {
                    const { r, g, b, a } = color.rgb;
                    handleStyleChange('backgroundColor', `rgba(${r}, ${g}, ${b}, ${a})`);
                  }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>테두리</label>
                <select 
                  value={selectedComponent.style.border || 'none'} 
                  onChange={(e) => handleStyleChange('border', e.target.value)}
                >
                  <option value="none">없음</option>
                  <option value="1px solid #e2e8f0">기본</option>
                  <option value="1px dashed #e2e8f0">점선</option>
                  <option value="2px solid #e2e8f0">두꺼운 실선</option>
                </select>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>모서리 반경</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    value={parseInt(selectedComponent.style.borderRadius || '4')} 
                    onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>
          </>
        );
      
      case COMPONENT_TYPES.COLUMN:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 3V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 3V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                열 속성
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>전체 너비</label>
                <div className="input-with-unit">
                  <input 
                    type="text" 
                    value={(selectedComponent.data?.width || '100%').replace('%', '')} 
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      let newWidth = value ? `${value}%` : '100%';
                      if (value.includes('px') || value.includes('rem') || value.includes('em') || value.includes('vh') || value.includes('vw')) {
                        newWidth = value;
                      } else if (value && !isNaN(value)) {
                        newWidth = `${value}%`;
                      }
                      handleDataChange('width', newWidth);
                    }}
                  />
                  <select 
                    value={(selectedComponent.data?.width || '100%').includes('%') ? '%' : 
                           (selectedComponent.data?.width || '').includes('px') ? 'px' : 
                           (selectedComponent.data?.width || '').includes('rem') ? 'rem' : 
                           (selectedComponent.data?.width || '').includes('vh') ? 'vh' : 
                           (selectedComponent.data?.width || '').includes('vw') ? 'vw' : '%'}
                    onChange={(e) => {
                      const currentValue = (selectedComponent.data?.width || '100%').replace(/[^0-9.]/g, '');
                      handleDataChange('width', `${currentValue}${e.target.value}`);
                    }}
                  >
                    <option value="%">%</option>
                    <option value="px">px</option>
                    <option value="rem">rem</option>
                    <option value="vh">vh</option>
                    <option value="vw">vw</option>
                  </select>
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>간격 (Gutter)</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    value={selectedComponent.data?.gutter || 0} 
                    min="0"
                    max="100"
                    onChange={(e) => handleDataChange('gutter', parseInt(e.target.value) || 0)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>반응형 배율</label>
                <div className="grid-row">
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>XS</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12" 
                      value={selectedComponent.data?.xs || 12} 
                      onChange={(e) => handleDataChange('xs', parseInt(e.target.value) || 12)}
                    />
                  </div>
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>SM</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12" 
                      value={selectedComponent.data?.sm || ''} 
                      onChange={(e) => {
                        const value = e.target.value.trim() === '' ? undefined : parseInt(e.target.value);
                        handleDataChange('sm', value);
                      }}
                    />
                  </div>
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>MD</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12" 
                      value={selectedComponent.data?.md || ''} 
                      onChange={(e) => {
                        const value = e.target.value.trim() === '' ? undefined : parseInt(e.target.value);
                        handleDataChange('md', value);
                      }}
                    />
                  </div>
                </div>
                <div className="grid-row">
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>LG</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12" 
                      value={selectedComponent.data?.lg || ''} 
                      onChange={(e) => {
                        const value = e.target.value.trim() === '' ? undefined : parseInt(e.target.value);
                        handleDataChange('lg', value);
                      }}
                    />
                  </div>
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>XL</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12" 
                      value={selectedComponent.data?.xl || ''} 
                      onChange={(e) => {
                        const value = e.target.value.trim() === '' ? undefined : parseInt(e.target.value);
                        handleDataChange('xl', value);
                      }}
                    />
                  </div>
                  <div className="grid-cell">
                    <label style={{ minWidth: '30%' }}>옵셋</label>
                    <input 
                      type="number" 
                      min="0" 
                      max="12"
                      value={selectedComponent.data?.offset || 0} 
                      onChange={(e) => handleDataChange('offset', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 3V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 3V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                배경 스타일
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <SketchPicker 
                  color={selectedComponent.style.backgroundColor || 'rgba(237, 242, 247, 0.5)'} 
                  onChange={(color) => {
                    const { r, g, b, a } = color.rgb;
                    handleStyleChange('backgroundColor', `rgba(${r}, ${g}, ${b}, ${a})`);
                  }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>테두리</label>
                <select 
                  value={selectedComponent.style.border || '1px solid #e2e8f0'} 
                  onChange={(e) => handleStyleChange('border', e.target.value)}
                >
                  <option value="none">없음</option>
                  <option value="1px solid #e2e8f0">기본</option>
                  <option value="1px dashed #e2e8f0">점선</option>
                  <option value="2px solid #e2e8f0">두꺼운 실선</option>
                </select>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>모서리 반경</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    value={parseInt(selectedComponent.style.borderRadius || '4')} 
                    onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>
          </>
        );
            
      case COMPONENT_TYPES.PRODUCT_LIST:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-row">
                <label style={{ minWidth: '30%' }}>재고 정보 표시</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-stock-list"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showStock === true}
                    onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
                  />
                  <label htmlFor="toggle-stock-list" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
            </div>

            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                필터링 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>포함할 상품 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.include_product_ids || ''}
                  onChange={(e) => handleComponentPropsChange('include_product_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 73,74,75)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제외할 상품 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.exclude_product_ids || ''}
                  onChange={(e) => handleComponentPropsChange('exclude_product_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 74,76)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>포함할 카테고리 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.include_category_ids || ''}
                  onChange={(e) => handleComponentPropsChange('include_category_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 14,8)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제외할 카테고리 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.exclude_category_ids || ''}
                  onChange={(e) => handleComponentPropsChange('exclude_category_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 16,17)
                </div>
              </div>
            </div>
            
            {/*
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                제품 목록 설정
              </div>
              
             <div className="property-row">
                <label style={{ minWidth: '30%' }}>한 줄당 제품 수</label>
                <div style={{marginBottom: '10px'}}>
                </div>
                 제품 수 선택 버튼 - PC에서는 항상 4개로 고정
                <div className="button-group" style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                  {[2, 3, 4, 6].map(num => (
                    <button
                      key={num}
                      style={{
                        padding: '8px 12px',
                        background: selectedComponent.componentProps?.itemsPerRow === num ? '#007bff' : '#f0f0f0',
                        color: selectedComponent.componentProps?.itemsPerRow === num ? 'white' : 'black',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        // 직접 dispatch 호출
            dispatch(updateComponent({
              id: selectedComponent.id,
              changes: {
                            componentProps: {
                              ...selectedComponent.componentProps,
                              itemsPerRow: num
                            }
                          }
                        }));
                      }}
                    >
                      {num}개
                    </button>
                  ))}
                </div>
              
              </div>
              
       
              
              
            </div>
            */}
          </>
        );
        
      case COMPONENT_TYPES.PRODUCT_DETAIL:
        return (
          <>
            {renderPaddingControls()}
            
            

            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                탭 표시 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>설명(Description) 탭</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-description-tab"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showDescriptionTab !== false}
                    onChange={(e) => handleComponentPropsChange('showDescriptionTab', e.target.checked)}
                  />
                  <label htmlFor="toggle-description-tab" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>리뷰(Reviews) 탭</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-reviews-tab"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showReviewsTab !== false}
                    onChange={(e) => handleComponentPropsChange('showReviewsTab', e.target.checked)}
                  />
                  <label htmlFor="toggle-reviews-tab" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div>
              
              {/* <div className="property-row">
                <label style={{ minWidth: '30%' }}>상세 스펙(Specification) 탭</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-specification-tab"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showSpecificationTab !== false}
                    onChange={(e) => handleComponentPropsChange('showSpecificationTab', e.target.checked)}
                  />
                  <label htmlFor="toggle-specification-tab" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div> */}
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>재고 정보 표시</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-stock-display"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showStock === true}
                    onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
                  />
                  <label htmlFor="toggle-stock-display" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div>
            </div>
          </>
        );
      
      case COMPONENT_TYPES.FAQ:
        // FAQ 아이템 관리 기능
        const handleFAQItemChange = (index, field, value) => {
          const updatedItems = [...(selectedComponent.faqItems || [])];
          updatedItems[index] = {
            ...updatedItems[index],
                    [field]: value
          };
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              faqItems: updatedItems
            }
          }));
        };
        
        const handleAddFAQItem = () => {
          const updatedItems = [
            ...(selectedComponent.faqItems || []),
            { question: '새 질문', answer: '새 답변' }
          ];
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              faqItems: updatedItems
            }
          }));
        };
        
        const handleRemoveFAQItem = (index) => {
          const updatedItems = [...(selectedComponent.faqItems || [])];
          updatedItems.splice(index, 1);
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              faqItems: updatedItems
              }
            }));
        };
        
        // FAQ 제목 변경 핸들러
        const handleTitleChange = (value) => {
            dispatch(updateComponent({
              id: selectedComponent.id,
              changes: {
              title: value
            }
          }));
        };
        
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 8.01L12.01 7.99889" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                FAQ 설정
              </div>
              
              {/* FAQ 제목 설정 */}
              <div className="property-control">
                <label>FAQ 제목</label>
                <input
                  type="text"
                  value={selectedComponent.title || "자주하는질문"}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="faq-title-input"
                />
              </div>
              
              {/* 제목 크기 설정 */}
              <div className="property-control">
                <label>제목 크기</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    value={parseInt((selectedComponent.style?.titleStyle?.fontSize || '24').replace('px', ''))} 
                    onChange={(e) => {
                      const fontSize = `${e.target.value}px`;
                      dispatch(updateComponent({
                        id: selectedComponent.id,
                        changes: {
                          style: {
                            ...selectedComponent.style,
                            titleStyle: {
                              ...(selectedComponent.style?.titleStyle || {}),
                              fontSize
                            }
                          }
                        }
                      }));
                    }}
                    min="12"
                    max="72"
                  />
                  <span className="unit">px</span>
                </div>
              </div>
              
              {/* 제목 정렬 설정 */}
              <div className="property-control">
                <label>제목 정렬</label>
                <select
                  value={(selectedComponent.style?.titleStyle?.textAlign) || "left"}
                  onChange={(e) => {
                    dispatch(updateComponent({
                      id: selectedComponent.id,
                      changes: {
                        style: {
                          ...selectedComponent.style,
                          titleStyle: {
                            ...(selectedComponent.style?.titleStyle || {}),
                            textAlign: e.target.value
                          }
                        }
                      }
                    }));
                  }}
                >
                  <option value="left">왼쪽</option>
                  <option value="center">가운데</option>
                  <option value="right">오른쪽</option>
                </select>
              </div>
              
              {/* FAQ 아이템 관리 */}
              <div className="property-control-section">
                <label>FAQ 항목</label>
                
                {(selectedComponent.faqItems || []).map((item, index) => (
                  <div key={index} className="faq-item-edit">
                    <div className="faq-item-header">
                      <span>질문 {index + 1}</span>
                      <button 
                        className="remove-faq-btn"
                        onClick={() => handleRemoveFAQItem(index)}
                      >
                        삭제
                      </button>
                    </div>
                    
                    <div className="property-control">
                      <label>질문</label>
                      <textarea
                        value={item.question}
                        onChange={(e) => handleFAQItemChange(index, 'question', e.target.value)}
                      />
                    </div>
                    
                    <div className="property-control">
                      <label>답변</label>
                      <textarea
                        value={item.answer}
                        onChange={(e) => handleFAQItemChange(index, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                
                <button
                  className="add-faq-btn"
                  onClick={handleAddFAQItem}
                >
                  질문 추가
                </button>
              </div>
            </div>
            
            {renderPaddingControls()}
          </>
        );
      
      case COMPONENT_TYPES.COMPANY_HISTORY:
        // 회사 연혁 아이템 관리 기능
        const handleHistoryItemChange = (index, field, value) => {
          const updatedItems = [...(selectedComponent.content?.historyItems || [])];
          updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
          };
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                historyItems: updatedItems
              }
            }
          }));
        };
        
        const handleAddHistoryItem = () => {
          const { v4: uuidv4 } = require('uuid');
          const updatedItems = [
            ...(selectedComponent.content?.historyItems || []),
            { 
              id: uuidv4(),
              year: new Date().getFullYear().toString(),
              title: '새 이벤트',
              description: '이벤트 설명을 입력하세요.',
              image: ''
            }
          ];
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                historyItems: updatedItems
              }
            }
          }));
        };
        
        const handleRemoveHistoryItem = (index) => {
          const updatedItems = [...(selectedComponent.content?.historyItems || [])];
          updatedItems.splice(index, 1);
          
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                historyItems: updatedItems
              }
            }
          }));
        };
        
        const handleHistoryTitleChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                title: value
              }
            }
          }));
        };
        
        const handleHistorySubtitleChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                subtitle: value
              }
            }
          }));
        };
        
        const handleTimelineStyleChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                timelineStyle: value
              }
            }
          }));
        };
        
        const handleAnimationChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                animation: value
              }
            }
          }));
        };
        
        const handleColorSchemeChange = (colorType, value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                colorScheme: {
                  ...(selectedComponent.content?.colorScheme || {
                    primary: '#3498db',
                    secondary: '#f1c40f',
                    background: '#ffffff',
                    text: '#333333'
                  }),
                  [colorType]: value
                }
              }
            }
          }));
        };
        
        const handleImageSizeChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                imageSize: value,
                imageWidth: value,
                imageHeight: value
              }
            }
          }));
        };
        
        const handleImageWidthChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                imageWidth: value
              }
            }
          }));
        };
        
        const handleImageHeightChange = (value) => {
          dispatch(updateComponent({
            id: selectedComponent.id,
            changes: {
              content: {
                ...selectedComponent.content,
                imageHeight: value
              }
            }
          }));
        };
        
        return (
          <>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                </svg>
                회사 연혁 설정
              </div>
              
              {/* 연혁 제목 설정 */}
              <div className="property-control">
                <label>제목</label>
                <input
                  type="text"
                  value={selectedComponent.content?.title || "회사 연혁"}
                  onChange={(e) => handleHistoryTitleChange(e.target.value)}
                  className="history-title-input"
                />
              </div>
              
              {/* 연혁 부제목 설정 */}
              <div className="property-control">
                <label>부제목</label>
                <textarea
                  value={selectedComponent.content?.subtitle || "저희 회사의 성장 과정을 소개합니다"}
                  onChange={(e) => handleHistorySubtitleChange(e.target.value)}
                  className="history-subtitle-input"
                />
              </div>
              
              {/* 타임라인 스타일 설정 */}
              {/* <div className="property-control">
                <label>타임라인 스타일</label>
                <select
                  value={selectedComponent.content?.timelineStyle || "vertical"}
                  onChange={(e) => handleTimelineStyleChange(e.target.value)}
                >
                  <option value="vertical">수직형</option>
                  <option value="horizontal">수평형</option>
                </select>
              </div> */}
              
              {/* 애니메이션 설정 */}
            <div className="property-row">
                <label>애니메이션 효과</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-animation"
                    type="checkbox" 
                    checked={selectedComponent.content?.animation !== false}
                    onChange={(e) => handleAnimationChange(e.target.checked)}
                  />
                  <label htmlFor="toggle-animation" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
            </div>

              {/* 이미지 크기 설정 */}
              <div className="property-control">
                <label>이미지 크기 설정</label>
                <div className="image-size-inputs">
                  <div className="image-dimension">
                    <label>가로 크기</label>
                    <div className="input-with-unit">
                      {(() => {
                        const imageWidth = selectedComponent.content?.imageWidth || selectedComponent.content?.imageSize || '150px';
                        const { value, unit } = extractValueAndUnit(imageWidth.toString());
                        return (
                          <>
                            <input 
                              type="number" 
                              value={value}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                if (!isNaN(newValue) && parseFloat(newValue) > 0) {
                                  handleImageWidthChange(`${newValue}${unit}`);
                                }
                              }}
                              min="10"
                              step="1"
                            />
                            <select 
                              value={unit}
                              onChange={(e) => {
                                const newUnit = e.target.value;
                                handleImageWidthChange(`${value}${newUnit}`);
                              }}
                            >
                              <option value="px">px</option>
                              <option value="%">%</option>
                              <option value="em">em</option>
                              <option value="rem">rem</option>
                              <option value="vw">vw</option>
                            </select>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="image-dimension">
                    <label>세로 크기</label>
                    <div className="input-with-unit">
                      {(() => {
                        const imageHeight = selectedComponent.content?.imageHeight || selectedComponent.content?.imageSize || '150px';
                        const { value, unit } = extractValueAndUnit(imageHeight.toString());
                        return (
                          <>
                            <input 
                              type="number" 
                              value={value}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                if (!isNaN(newValue) && parseFloat(newValue) > 0) {
                                  handleImageHeightChange(`${newValue}${unit}`);
                                }
                              }}
                              min="10"
                              step="1"
                            />
                            <select 
                              value={unit}
                              onChange={(e) => {
                                const newUnit = e.target.value;
                                handleImageHeightChange(`${value}${newUnit}`);
                              }}
                            >
                              <option value="px">px</option>
                              <option value="%">%</option>
                              <option value="em">em</option>
                              <option value="rem">rem</option>
                              <option value="vh">vh</option>
                            </select>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* 미리 정의된 크기 버튼 */}
                <div className="preset-size-buttons">
                  <button
                    className="preset-size-btn"
                    onClick={() => {
                      handleImageWidthChange('100px');
                      handleImageHeightChange('100px');
                    }}
                  >
                    작게 (100×100)
                  </button>
                  <button
                    className="preset-size-btn"
                    onClick={() => {
                      handleImageWidthChange('150px');
                      handleImageHeightChange('150px');
                    }}
                  >
                    중간 (150×150)
                  </button>
                  <button
                    className="preset-size-btn"
                    onClick={() => {
                      handleImageWidthChange('200px');
                      handleImageHeightChange('200px');
                    }}
                  >
                    크게 (200×200)
                  </button>
                </div>
              </div>
              
              {/* 연혁 아이템 관리 */}
              <div className="property-control-section">
                <div className="section-header">
                  <label>연혁 항목</label>
                  <button 
                    className="add-item-btn"
                    onClick={handleAddHistoryItem}
                  >
                    항목 추가
                  </button>
                </div>
                
                {(selectedComponent.content?.historyItems || []).map((item, index) => (
                  <div key={item.id || index} className="history-item-edit">
                    <div className="history-item-header">
                      <span>연혁 {index + 1}</span>
                      <button 
                        className="remove-history-btn"
                        onClick={() => handleRemoveHistoryItem(index)}
                      >
                        삭제
                      </button>
                    </div>
                    
                    <div className="property-control">
                      <label>연도</label>
                      <input
                        type="text"
                        value={item.year}
                        onChange={(e) => handleHistoryItemChange(index, 'year', e.target.value)}
                      />
                    </div>
                    
                    <div className="property-control">
                      <label>제목</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleHistoryItemChange(index, 'title', e.target.value)}
                      />
                    </div>
                    
                    <div className="property-control">
                      <label>설명</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleHistoryItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                    
                    <div className="property-control">
                      <label>이미지 URL</label>
                      <div className="image-url-input-container">
                        <div className="image-url-input-group">
                          <input 
                            type="text" 
                            placeholder="이미지 URL을 입력하세요"
                            value={item.image || ''}
                            onChange={(e) => handleHistoryItemChange(index, 'image', e.target.value)}
                            className="image-url-input"
                          />
                          {item.image && (
                            <button 
                              className="clear-image-btn"
                              onClick={() => handleHistoryItemChange(index, 'image', '')}
                              title="이미지 삭제"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <div className="image-actions-container">
                          <label className="upload-image-btn">
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  try {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    formData.append('bucket', 'withcookie-bucket');
                                    formData.append('path', 'c/default');
                                    
                                    const res = await axios.post(
                                      `https://refrigerator.logipasta.com/v1/file`,
                                      formData,
                                      {
                                        headers: {
                                          'Content-Type': 'multipart/form-data',
                                          'Access-Control-Allow-Origin': '*',
                                        },
                                      }
                                    );
                                    
                                    if (res.data && res.data.file) {
                                      handleHistoryItemChange(index, 'image', res.data.file);
                                      alert('이미지가 성공적으로 업로드되었습니다.');
                                    }
                                  } catch (err) {
                                    console.error('[연혁] 이미지 업로드 실패:', err);
                                    alert('이미지 업로드에 실패했습니다.');
                                  }
                                }
                              }}
                            />
                            <span>이미지 업로드</span>
                          </label>
                        </div>
                        {item.image && (
                          <div className="image-preview">
                            <img src={item.image} alt={`연혁 ${index + 1} 이미지`} />
                            <div className="image-size-badge">
                              {selectedComponent.content?.imageSize || 'medium'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        );
      
      case COMPONENT_TYPES.MILEAGE_HISTORY:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                마일리지 내역 설정
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제목</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.title || '마일리지 내역'} 
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                  style={{ width: '100%' }}
                />
                </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>내역 없음 메시지</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.emptyText || '마일리지 내역이 없습니다.'} 
                  onChange={(e) => handleComponentPropsChange('emptyText', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            {renderPaddingControls()}
          </>
        );
        
      case COMPONENT_TYPES.MEMBERSHIP_SIGNUP:
        return (
          <>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                멤버십 가입 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>상품 ID</label>
                <input 
                  type="number" 
                  min="1"
                  value={selectedComponent.componentProps?.productId || ""} 
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value ? parseInt(value, 10) : 0;
                    handleComponentPropsChange('productId', numValue);
                  }}
                  style={{ width: '100%' }}
                  placeholder="예: 58"
                />
                <div className="property-description">
                  사용할 상품의 ID를 입력하세요. (API 호출 시 products/ID 형식으로 사용됩니다)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제목</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.title || '멤버십 가입신청'} 
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                  style={{ width: '100%' }}
                />
                </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>설명</label>
                <textarea 
                  value={selectedComponent.componentProps?.description || '멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다.'} 
                  onChange={(e) => handleComponentPropsChange('description', e.target.value)}
                  style={{ width: '100%', minHeight: '60px' }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>가입 버튼 텍스트</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.buttonText || '가입 신청'} 
                  onChange={(e) => handleComponentPropsChange('buttonText', e.target.value)}
                  style={{ width: '100%' }}
                />
                </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>완료 텍스트</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.completedText || '가입 완료'} 
                  onChange={(e) => handleComponentPropsChange('completedText', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                스타일 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <input 
                  type="color" 
                  value={selectedComponent.style?.backgroundColor || '#ffffff'} 
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>테두리 반경</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    min="0"
                    value={parseInt(selectedComponent.style?.borderRadius || '8')} 
                    onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>
          </>
        );
      case COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP:
        return (
          <>
            {renderPaddingControls()}
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                자유 멤버십 가입 설정
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>결제 가능한 자산</label>
                <div style={{ width: '100%' }}>
                  {(selectedComponent.componentProps?.assetOptions || []).map((opt, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      <input
                        type="text"
                        placeholder="자산 ID"
                        value={opt.id || ''}
                        onChange={e => {
                          const newOptions = [...(selectedComponent.componentProps?.assetOptions || [])];
                          newOptions[idx] = { ...newOptions[idx], id: e.target.value };
                          handleComponentPropsChange('assetOptions', newOptions);
                        }}
                        style={{ width: '30%' }}
                      />
                      <button type="button" onClick={() => {
                        const newOptions = [...(selectedComponent.componentProps?.assetOptions || [])];
                        newOptions.splice(idx, 1);
                        handleComponentPropsChange('assetOptions', newOptions);
                      }}>삭제</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    const newOptions = [...(selectedComponent.componentProps?.assetOptions || []), { id: '', name: '' }];
                    handleComponentPropsChange('assetOptions', newOptions);
                  }}>+ 자산 추가</button>
                </div>
              </div>              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>상품 ID</label>
                <input 
                  type="number" 
                  min="1"
                  value={selectedComponent.componentProps?.productId || ""} 
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = value ? parseInt(value, 10) : 0;
                    handleComponentPropsChange('productId', numValue);
                  }}
                  style={{ width: '100%' }}
                  placeholder="예: 58"
                />
                <div className="property-description">
                  사용할 상품의 ID를 입력하세요. (API 호출 시 products/ID 형식으로 사용됩니다)
                </div>
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제목</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.title || '자유 멤버십 가입'} 
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>설명</label>
                <textarea 
                  value={selectedComponent.componentProps?.description || '직접 금액을 입력해 가입/추가 결제할 수 있습니다.'} 
                  onChange={(e) => handleComponentPropsChange('description', e.target.value)}
                  style={{ width: '100%', minHeight: '60px' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>버튼 텍스트</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.buttonText || '가입/추가 결제'} 
                  onChange={(e) => handleComponentPropsChange('buttonText', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>완료 텍스트</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.completedText || '추가 결제가 완료되었습니다.'} 
                  onChange={(e) => handleComponentPropsChange('completedText', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>환급 시각화</label>
                <input 
                  type="checkbox" 
                  checked={selectedComponent.componentProps?.showRefundVisualization !== false}
                  onChange={(e) => handleComponentPropsChange('showRefundVisualization', e.target.checked)}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>환급 한도 퍼센트</label>
                <input 
                  type="number" 
                  min="1"
                  value={selectedComponent.componentProps?.refundLimitPercent || 400} 
                  onChange={(e) => handleComponentPropsChange('refundLimitPercent', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>최소 가입 가능 금액</label>
                <input 
                  type="number" 
                  min="1"
                  value={selectedComponent.componentProps?.minAmount || 10000} 
                  onChange={(e) => handleComponentPropsChange('minAmount', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>
              {/* assetOptions 등 추가 필요시 여기에 구현 */}
            </div>
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                스타일 설정
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <input 
                  type="color" 
                  value={selectedComponent.style?.backgroundColor || '#ffffff'} 
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>테두리 반경</label>
                <div className="input-with-unit">
                  <input 
                    type="number" 
                    min="0"
                    value={parseInt(selectedComponent.style?.borderRadius || '8')} 
                    onChange={(e) => handleStyleChange('borderRadius', `${e.target.value}px`)}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            </div>
          </>
        );      
      case COMPONENT_TYPES.BINARY_LEGS:
        // 바이너리 레그 컴포넌트 속성
        return (
          <>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4v2M12 18v2M6 12H4M20 12h-2M17.7 6.3l-1.4 1.4M6.3 17.7l1.4-1.4M17.7 17.7l-1.4-1.4M6.3 6.3l1.4 1.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
                바이너리 레그 설정
              </div>
              
              <div className="property-control">
                <label>제목</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.title || "후원 레그"}
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                />
                </div>
              </div>
          </>
        );

      case COMPONENT_TYPES.RICH_EDITOR:
        // 리치 에디터 컴포넌트 속성
        // 상위 레벨에서 정의된 isEditorModalOpen 상태 사용
        return (
          <div>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 7H4V5h10v2zm6 4H4v-2h16v2zm-6 4H4v-2h10v2zm6 4H4v-2h16v2z" fill="currentColor"/>
                </svg>
                텍스트 에디터 설정
            </div>
            
              <div className="property-control">
                <label>HTML 내용 미리보기</label>
                {/* <div className="html-content-preview">
                  {selectedComponent.content ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedComponent.content.slice(0, 150) + (selectedComponent.content.length > 150 ? '...' : '') }} />
                  ) : (
                    <div className="empty-content">내용이 없습니다. 아래 '에디터 열기' 버튼을 클릭하여 내용을 추가하세요.</div>
                  )}
                </div> */}

<div className="html-content-preview">
  {selectedComponent.content ? (
    <div dangerouslySetInnerHTML={{ 
      __html: typeof selectedComponent.content === 'string' 
        ? selectedComponent.content.slice(0, 150) + (selectedComponent.content.length > 150 ? '...' : '') 
        : '객체 형식의 콘텐츠입니다. 에디터를 열어서 확인하세요.' 
    }} />
  ) : (
    <div className="empty-content">내용이 없습니다. 아래 '에디터 열기' 버튼을 클릭하여 내용을 추가하세요.</div>
  )}
</div>
              </div>
              
              <div className="property-control">
                {/* <button 
                  className="editor-open-button"
                  onClick={() => setIsEditorModalOpen(true)}
                >
                  에디터 열기
                </button>
                 */}
                
                <button 
  className="editor-open-button"
  // onClick={() => {
  //   // 현재 콘텐츠 가져오기
  //   const currentContent = selectedComponent.content || '';
  //   // 리치 에디터로 편집하기 위해 상태 설정
  //   setRichEditorContent(currentContent);
  //   // 리치 에디터 모달 열기
  //   setIsRichEditorOpen(true);
  // }}

  onClick={() => {
    // 현재 콘텐츠 가져오기
    const currentContent = typeof selectedComponent.content === 'string' 
      ? selectedComponent.content 
      : '';
    // 리치 에디터로 편집하기 위해 상태 설정
    setRichEditorContent(currentContent);
    // 리치 에디터 모달 열기
    setIsRichEditorOpen(true);
  }}
>
  에디터 열기
</button>

                <button 
                  className="html-editor-button"
                  onClick={() => {
                    // HTML 에디터 예시 내용 추가
                    const exampleHtml = `
<h2>제목을 입력하세요</h2>
<p>이곳에 내용을 입력하세요. <strong>굵게</strong> 또는 <em>기울임꼴</em>로 강조할 수 있습니다.</p>
<ul>
  <li>항목 1</li>
  <li>항목 2</li>
  <li>항목 3</li>
</ul>
<p><a href="https://example.com">링크 텍스트</a></p>
<blockquote>인용구를 입력하세요.</blockquote>
                    `.trim();
                    handleContentChange(exampleHtml);
                  }}
                >
                  샘플 콘텐츠 추가
                </button>
              </div>
            </div>
            
            {/* 리치 에디터 모달 */}
            {isRichEditorOpen && (
              <RichEditorModal 
                isOpen={isRichEditorOpen}
                onClose={() => setIsRichEditorOpen(false)}
                initialContent={richEditorContent}
                onSave={handleRichEditorSave}
              />
            )}
          </div>
        );
      
      case COMPONENT_TYPES.PRODUCT_SLIDER:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                슬라이더 설정
              </div>
              
              {/* 슬라이더 타이틀 설정 */}
             <div className="property-row">
                <label style={{ minWidth: '30%' }}>슬라이더 타이틀</label>
                <input 
                  type="text"
                  value={selectedComponent.componentProps?.sliderTitle || '인기 상품'}
                  onChange={(e) => handleComponentPropsChange('sliderTitle', e.target.value)}
                  style={{ width: '100%' }}
                />
                </div>
              
              {/* 타이틀 표시 여부 */}
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>타이틀 표시</label>
                <div className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="toggle-slider-title"
                    checked={selectedComponent.componentProps?.showTitle !== false}
                    onChange={(e) => handleComponentPropsChange('showTitle', e.target.checked)}
                  />
                  <label htmlFor="toggle-slider-title"></label>
                </div>
              </div>
              
              {/* 타이틀 글자 크기 */}
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>타이틀 글자 크기</label>
                <div className="input-with-unit">
                  <input 
                    type="text" 
                    value={(selectedComponent.componentProps?.titleFontSize || '18px').replace('px', '')}
                    onChange={(e) => handleComponentPropsChange('titleFontSize', `${e.target.value}px`)}
                    style={{ width: '50px' }}
                  />
                  <span>px</span>
                </div>
              </div>
              
              {/* 타이틀 색상 */}
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>타이틀 색상</label>
                <input 
                  type="color"
                  value={selectedComponent.componentProps?.titleColor || '#333333'}
                  onChange={(e) => handleComponentPropsChange('titleColor', e.target.value)}
                />
              </div>
              
              <div className="property-control">
                <label>상품 ID 목록(쉼표로 구분)</label>
                <input 
                  type="text" 
                  placeholder="예: 1,2,3,4"
                  value={selectedComponent.componentProps?.productIds || ''}
                  onChange={(e) => handleComponentPropsChange('productIds', e.target.value)}
                />
                <small style={{ marginTop: '4px', display: 'block', color: '#6b7280', fontSize: '12px' }}>
                  여러 상품 ID를 쉼표(,)로 구분하여 입력하세요. 순서대로 슬라이더에 표시됩니다.
                </small>
              </div>
              {/* <div className="property-control">
                <label>슬라이드당 상품 수</label>
                <select 
                  value={selectedComponent.componentProps?.itemsPerSlide || 4}
                  onChange={(e) => handleComponentPropsChange('itemsPerSlide', parseInt(e.target.value))}
                >
                  <option value="1">1개</option>
                  <option value="2">2개</option>
                  <option value="3">3개</option>
                  <option value="4">4개</option>
                  <option value="5">5개</option>
                </select>
              </div> */}
              <div className="property-control">
                <label>이미지 높이 (px)</label>
                <input 
                  type="number" 
                  min="100"
                  max="500"
                  value={selectedComponent.componentProps?.imageHeight || 250}
                  onChange={(e) => handleComponentPropsChange('imageHeight', parseInt(e.target.value))}
                />
              </div>
              <div className="property-control">
                <label>자동 슬라이드</label>
                <div className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="autoSlide" 
                    checked={selectedComponent.componentProps?.autoSlide !== false}
                    onChange={(e) => handleComponentPropsChange('autoSlide', e.target.checked)}
                  />
                  <label htmlFor="autoSlide"></label>
                </div>
              </div>
              {selectedComponent.componentProps?.autoSlide !== false && (
                <div className="property-control">
                  <label>자동 슬라이드 간격 (ms)</label>
                  <select 
                    value={selectedComponent.componentProps?.autoSlideInterval || 5000}
                    onChange={(e) => handleComponentPropsChange('autoSlideInterval', parseInt(e.target.value))}
                  >
                    <option value="2000">2초</option>
                    <option value="3000">3초</option>
                    <option value="4000">4초</option>
                    <option value="5000">5초</option>
                    <option value="7000">7초</option>
                    <option value="10000">10초</option>
                  </select>
                </div>
              )}
              <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 9V21" stroke="currentColor" strokeWidth="2" />
                  <path d="M15 9V21" stroke="currentColor" strokeWidth="2" />
                </svg>
                필터링 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>포함할 상품 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.include_product_ids || ''}
                  onChange={(e) => handleComponentPropsChange('include_product_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 73,74,75)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제외할 상품 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.exclude_product_ids || ''}
                  onChange={(e) => handleComponentPropsChange('exclude_product_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 74,76)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>포함할 카테고리 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.include_category_ids || ''}
                  onChange={(e) => handleComponentPropsChange('include_category_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 14,8)
                </div>
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>제외할 카테고리 ID</label>
                <input 
                  type="text" 
                  value={selectedComponent.componentProps?.exclude_category_ids || ''}
                  onChange={(e) => handleComponentPropsChange('exclude_category_ids', e.target.value)}
                />
                <div className="help-text" style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  쉼표로 구분하여 입력 (예: 16,17)
                </div>
              </div>
            </div>
              
              {/* <div className="property-control">
                <label>재고 정보 표시</label>
                <div className="toggle-switch">
                  <input 
                    type="checkbox" 
                    id="toggle-stock-slider" 
                    checked={selectedComponent.componentProps?.showStock === true}
                    onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
                  />
                  <label htmlFor="toggle-stock-slider"></label>
                </div>
              </div> */}
            </div>
            {renderPaddingControls()}
            {renderBorderRadiusControl()}
            {renderStyleControls()}
            
          </>
        );
        
      case COMPONENT_TYPES.TRENDING_ITEMS:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-chart-line"></i> 트렌딩 아이템 설정
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>제목</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'title', 'Trending now')}
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>부제목</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'subtitle', '에디터가 선정한')}
                  onChange={(e) => handleComponentPropsChange('subtitle', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>이미지 둥글기</label>
                <div className="input-with-unit">
                  {(() => {
                    const imageRadius = getDevicePropertyValue(selectedComponent.componentProps, 'imageRadius', '4px');
                    const { value, unit } = extractValueAndUnit(imageRadius);
                    return (
                      <>
                        <input 
                          type="number" 
                          value={value} 
                          onChange={(e) => handleComponentPropsChange('imageRadius', `${e.target.value}${unit}`)}
                        />
                        <select 
                          value={unit}
                          onChange={(e) => {
                            const newUnit = e.target.value;
                            handleComponentPropsChange('imageRadius', `${value}${newUnit}`);
                          }}
                        >
                          <option value="px">px</option>
                          <option value="%">%</option>
                          <option value="rem">rem</option>
                        </select>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            {/* MORE 버튼 설정 추가 */}
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-external-link-alt"></i> MORE 버튼 설정
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>버튼 표시</label>
                <input 
                  type="checkbox" 
                  checked={getDevicePropertyValue(selectedComponent.componentProps, 'showMoreButton', true)}
                  onChange={(e) => handleComponentPropsChange('showMoreButton', e.target.checked)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>버튼 텍스트</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonText', 'MORE')}
                  onChange={(e) => handleComponentPropsChange('moreButtonText', e.target.value)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>버튼 URL</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonUrl', '#')}
                  onChange={(e) => handleComponentPropsChange('moreButtonUrl', e.target.value)}
                  placeholder="예: /products 또는 https://example.com"
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>버튼 배경색</label>
                <div className="color-picker-container">
                  <SketchPicker 
                    color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonBgColor', '#f8f8f8')}
                    onChange={(color) => handleComponentPropsChange('moreButtonBgColor', color.hex)}
                    presetColors={['#f8f8f8', '#e0e0e0', '#d2c3aa', '#8a7a6d', '#333333']}
                    disableAlpha={false}
                  />
                </div>
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>버튼 텍스트 색상</label>
                <div className="color-picker-container">
                  <SketchPicker 
                    color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonTextColor', '#8a7a6d')}
                    onChange={(color) => handleComponentPropsChange('moreButtonTextColor', color.hex)}
                    presetColors={['#8a7a6d', '#333333', '#666666', '#ffffff', '#000000']}
                    disableAlpha={false}
                  />
                </div>
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>호버 배경색</label>
                <div className="color-picker-container">
                  <SketchPicker 
                    color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonHoverBgColor', '#8a7a6d')}
                    onChange={(color) => handleComponentPropsChange('moreButtonHoverBgColor', color.hex)}
                    presetColors={['#8a7a6d', '#6a5a4d', '#333333', '#000000', '#d2c3aa']}
                    disableAlpha={false}
                  />
                </div>
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>호버 텍스트 색상</label>
                <div className="color-picker-container">
                  <SketchPicker 
                    color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonHoverTextColor', '#ffffff')}
                    onChange={(color) => handleComponentPropsChange('moreButtonHoverTextColor', color.hex)}
                    presetColors={['#ffffff', '#f8f8f8', '#e0e0e0', '#000000', '#333333']}
                    disableAlpha={false}
                  />
                </div>
              </div>
            </div>
            
            {/* 아이템 설정 */}
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-th-large"></i> 아이템 설정
              </div>
              
              {(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || []).map((item, index) => (
                <div key={index} className="item-editor" style={{ 
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  marginBottom: '10px'
                }}>
                  <div className="property-group-title" style={{ marginTop: 0 }}>
                    아이템 #{index+1}
                  </div>
                  
                  <div className="property-group">
                    <label style={{ minWidth: '30%' }}>이미지 URL</label>
                    <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        value={item.image} 
                        onChange={(e) => {
                          const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                          newItems[index] = { ...newItems[index], image: e.target.value };
                          handleComponentPropsChange('items', newItems);
                        }}
                        style={{ flex: 1 }}
                      />
                      <label className="upload-button" style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#4a90e2', 
                        color: 'white', 
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        업로드
                        <input 
                          type="file" 
                          style={{ display: 'none' }} 
                          accept="image/*"
                          onChange={async (e) => {
                            try {
                              const imageUrl = await handleImageUpload(e);
                              if (imageUrl) {
                                const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                                newItems[index] = { ...newItems[index], image: imageUrl };
                                handleComponentPropsChange('items', newItems);
                              }
                            } catch (error) {
                              console.error("이미지 업로드 오류:", error);
                              alert("이미지 업로드 중 오류가 발생했습니다.");
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  
                  <div className="property-group">
                    <label style={{ minWidth: '30%' }}>제목</label>
                    <input 
                      type="text" 
                      value={item.title} 
                      onChange={(e) => {
                        const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                        newItems[index] = { ...newItems[index], title: e.target.value };
                        handleComponentPropsChange('items', newItems);
                      }}
                    />
                  </div>
                  
                  <div className="property-group">
                    <label style={{ minWidth: '30%' }}>링크 URL</label>
                    <input 
                      type="text" 
                      value={item.url} 
                      onChange={(e) => {
                        const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                        newItems[index] = { ...newItems[index], url: e.target.value };
                        handleComponentPropsChange('items', newItems);
                      }}
                    />
                  </div>
                </div>
              ))}
              
                    <button
                onClick={() => {
                  const items = getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [];
                  const newItem = {
                    id: Date.now(),
                    image: "https://via.placeholder.com/400x400",
                    title: "새 아이템",
                    url: "#"
                  };
                  handleComponentPropsChange('items', [...items, newItem]);
                }}
                      style={{
                        padding: '8px 12px',
                  backgroundColor: '#4CAF50', 
                  color: 'white', 
                  border: 'none',
                        borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: '10px'
                }}
              >
                아이템 추가
              </button>
              
              {(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || []).length > 0 && (
                <button 
                      onClick={() => {
                    const items = getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [];
                    if (items.length > 0) {
                      const newItems = items.slice(0, -1);
                      handleComponentPropsChange('items', newItems);
                    }
                  }}
                  style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f44336', 
                    color: 'white', 
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  마지막 아이템 삭제
                </button>
              )}
            </div>

            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-paint-brush"></i> 스타일 설정
              </div>
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>배경색</label>
                <SketchPicker 
                  color={getDevicePropertyValue(selectedComponent.style, 'backgroundColor', 'transparent')} 
                  onChange={(color) => handleStyleChange('backgroundColor', color.hex)}
                />
              </div>
              
              <div className="property-group">
                <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
                <input 
                  type="text" 
                  value={getDevicePropertyValue(selectedComponent.style, 'borderRadius', '0px')}
                  onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
                />
              </div>
            </div>
          </>
        );
      
      case COMPONENT_TYPES.CART:
        return (
          <>
            <div className="property-group">
              <div className="property-group-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3H5.5L6 5M6 5H21L17 14H8M6 5L8 14M8 14L6.5 19H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="8.5" cy="21.5" r="1.5" stroke="currentColor"/>
                  <circle cx="17.5" cy="21.5" r="1.5" stroke="currentColor"/>
                </svg>
                장바구니 설정
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>타이틀</label>
                <input 
                  type="text"
                  value={selectedComponent.componentProps?.title || '장바구니'}
                  onChange={(e) => handleComponentPropsChange('title', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>빈 장바구니 메시지</label>
                <input 
                  type="text"
                  value={selectedComponent.componentProps?.emptyText || '장바구니가 비어 있습니다.'}
                  onChange={(e) => handleComponentPropsChange('emptyText', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div className="property-row">
                <label style={{ minWidth: '30%' }}>배송비 (원)</label>
                <input 
                  type="number"
                  min="0"
                  value={selectedComponent.componentProps?.shippingFee || 4000}
                  onChange={(e) => handleComponentPropsChange('shippingFee', parseInt(e.target.value, 10))}
                  style={{ width: '100%' }}
                />
              </div>
              
              {/* <div className="property-row">
                <label style={{ minWidth: '30%' }}>재고 정보 표시</label>
                <div className="custom-toggle-switch">
                  <input 
                    id="toggle-stock-cart"
                    type="checkbox" 
                    checked={selectedComponent.componentProps?.showStock === true}
                    onChange={(e) => handleComponentPropsChange('showStock', e.target.checked)}
                  />
                  <label htmlFor="toggle-stock-cart" className="toggle-switch-label">
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div> */}
            </div>
            
            {renderPaddingControls()}
            {renderBorderRadiusControl()}
            {renderStyleControls()}
          </>
        );
      
      case COMPONENT_TYPES.PARALLAX:
        return (
          <>
            {renderPaddingControls()}
            
            <div className="property-group">
              <div className="property-group-title">
                <i className="fas fa-magic"></i> Parallax 효과 설정
              </div>
              <div className="property-control">
                <label>배경 이동 속도</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.05" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'parallaxSpeed', 0.3)} 
                    onChange={(e) => handleComponentPropsChange('parallaxSpeed', parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span>{(getDevicePropertyValue(selectedComponent.componentProps, 'parallaxSpeed', 0.3)).toFixed(2)}</span>
                </div>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  값이 클수록 배경 이미지가 더 많이 움직입니다. 0으로 설정하면 배경이 고정됩니다.
                </small>
              </div>
              
              <div className="property-control">
                <label>배경 시작 위치</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="range" 
                    min="-20" 
                    max="20" 
                    step="5" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'startPosition', 0)} 
                    onChange={(e) => handleComponentPropsChange('startPosition', parseInt(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span>{getDevicePropertyValue(selectedComponent.componentProps, 'startPosition', 0)}%</span>
                </div>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  스크롤 시작 시 배경 이미지 위치입니다. (음수: 위로 이동, 양수: 아래로 이동)
                </small>
              </div>
              
              <div className="property-control">
                <label>배경 끝 위치</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input 
                    type="range" 
                    min="60" 
                    max="120" 
                    step="5" 
                    value={getDevicePropertyValue(selectedComponent.componentProps, 'endPosition', 80)} 
                    onChange={(e) => handleComponentPropsChange('endPosition', parseInt(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span>{getDevicePropertyValue(selectedComponent.componentProps, 'endPosition', 80)}%</span>
                </div>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  스크롤 끝에서의 배경 이미지 위치입니다. (값이 클수록 더 많이 이동합니다)
                </small>
              </div>
            </div>
          </>
        );
      
      default:
        return <div>알 수 없는 컴포넌트 타입입니다.</div>;
    }
  };
  
  // 이미지 업로드 핸들러 추가
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return null;

    try {
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default'); // 필요시 회사 코드 수정

      
      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
          timeout: 30000, // 30초 타임아웃 추가
        }
      );

      // 응답 확인

      if (res.data && res.data.file) {
        // 업로드 성공
        alert('이미지가 성공적으로 업로드되었습니다.');
        
        // 반환값 설정
        return res.data.file;
      } else {
        console.error('이미지 URL이 응답에 없습니다:', res.data);
        alert('서버 응답에 이미지 URL이 포함되어 있지 않습니다. 관리자에게 문의하세요.');
        return null;
      }
    } catch (err) {
      console.error('이미지 업로드 실패:', err);
      
      // 더 자세한 오류 메시지 표시
      let errorMessage = '이미지 업로드에 실패했습니다. ';
      
      if (err.response) {
        // 서버에서 응답이 왔지만 오류 상태 코드를 반환한 경우
        console.error('서버 응답 오류:', err.response.status, err.response.data);
        errorMessage += `서버 응답 오류 (${err.response.status}): ${JSON.stringify(err.response.data)}`;
      } else if (err.request) {
        // 요청은 전송되었지만 응답이 없는 경우
        console.error('서버 응답 없음:', err.request);
        errorMessage += '서버로부터 응답이 없습니다. 네트워크 연결을 확인해주세요.';
      } else if (err.code === 'ECONNABORTED') {
        // 타임아웃 오류
        errorMessage += '요청 시간이 초과되었습니다. 나중에 다시 시도해주세요.';
      } else {
        // 요청 설정 중 오류가 발생한 경우
        console.error('요청 오류:', err.message);
        errorMessage += err.message;
      }
      
      alert(errorMessage);
      return null;
    }
  };
  
  // 패딩 컨트롤을 디바이스별로 분리하여 렌더링
  const renderBoardPaddingControls = () => {
    // 현재 디바이스에 맞는 패딩값 가져오기
    const getDevicePadding = (paddingType) => {
      try {
        const styleKey = `padding${paddingType}`;
        
        // 스타일 객체에서 디바이스별 설정 확인
        if (selectedComponent.style?.device && 
            selectedComponent.style.device[boardDeviceTab] && 
            selectedComponent.style.device[boardDeviceTab][styleKey] !== undefined) {
          return selectedComponent.style.device[boardDeviceTab][styleKey];
        }
        
        // 디바이스별 설정이 없는 경우 기본 스타일 사용
        return selectedComponent.style?.[styleKey] || '0px';
      } catch (err) {
        console.error(`패딩값 가져오기 오류:`, err);
        return '0px';
      }
    };
    
    // 패딩 값 변경 핸들러
    const handleDevicePaddingChange = (paddingType, value) => {
      try {
        const styleKey = `padding${paddingType}`;
        
        // 기존 스타일의 깊은 복사
        const existingStyle = JSON.parse(JSON.stringify(selectedComponent.style || {}));
        
        // 디바이스별 스타일 객체 초기화
        if (!existingStyle.device) {
          existingStyle.device = { pc: {}, mobile: {} };
          
          // 기존 패딩 값이 있으면 디바이스 객체로 복사
          ['Top', 'Right', 'Bottom', 'Left'].forEach(side => {
            const key = `padding${side}`;
            if (existingStyle[key]) {
              existingStyle.device.pc[key] = existingStyle[key];
              existingStyle.device.mobile[key] = existingStyle[key];
            }
          });
        }
        
        // 현재 디바이스 스타일 초기화 (필요시)
        if (!existingStyle.device[boardDeviceTab]) {
          existingStyle.device[boardDeviceTab] = {};
        }
        
        // 현재 디바이스 스타일 업데이트
        existingStyle.device[boardDeviceTab][styleKey] = value;
        
        // 현재 선택된 디바이스의 스타일을 최상위 스타일에도 적용 (즉시 반영을 위해)
        existingStyle[styleKey] = value;
        
        
        // 전체 스타일 업데이트 - 직접 스타일 변경
        requestAnimationFrame(() => {
            dispatch(updateComponent({
              id: selectedComponent.id,
              changes: {
              style: existingStyle
            }
          }));
          
          // 변경 적용 후 강제로 스타일 재적용
          setTimeout(() => {
            ensureDeviceStylesApplied();
          }, 50);
        });
      } catch (err) {
        console.error(`패딩 값 변경 중 오류:`, err);
      }
    };
    
    // 패딩 입력 변경 핸들러
    const handlePaddingInputChange = (paddingType, inputValue, currentStyle) => {
      try {
        // 입력값이 비어있으면 처리하지 않음 (사용자가 값을 지울 수 있도록)
        if (inputValue === '') {
          return;
        }
        
        // 입력값에서 단위와 값 직접 추출 (em, px 등 단위 포함 입력 지원)
        const unitRegex = /(-?\d*\.?\d+)([a-z%]+)$/i;
        const unitMatch = inputValue.match(unitRegex);
        
        // 유효한 단위 목록
        const validUnits = ['px', '%', 'em', 'rem', 'vh', 'vw'];
        
        if (unitMatch && unitMatch[2] && validUnits.includes(unitMatch[2].toLowerCase())) {
          // 단위가 포함된 입력값 처리 (10px, 1.5em 등)
          const numericValue = parseFloat(unitMatch[1]);
          const unit = unitMatch[2].toLowerCase();
          
          if (!isNaN(numericValue)) {
            handleDevicePaddingChange(paddingType, `${numericValue}${unit}`);
            return;
          }
        }
        
        // 숫자만 있는 경우 기존 단위 유지
        const numericRegex = /^-?\d*\.?\d+$/;
        const numericMatch = inputValue.match(numericRegex);
        
        if (numericMatch) {
          const numericValue = parseFloat(numericMatch[0]);
          const { unit } = extractValueAndUnit(currentStyle);
          
          handleDevicePaddingChange(paddingType, `${numericValue}${unit}`);
          return;
        }
        
        // 입력값에서 단위 검색
        const detectedUnit = detectUnitInText(inputValue);
        if (detectedUnit) {
          // 단위를 제외한 숫자 부분만 추출
          const numericValue = parseFloat(inputValue.replace(detectedUnit, ''));
          
          if (!isNaN(numericValue)) {
            handleDevicePaddingChange(paddingType, `${numericValue}${detectedUnit}`);
          }
        }
      } catch (err) {
        console.error(`패딩 입력 변경 중 오류:`, err);
      }
    };
    
    return (
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 17H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          여백 설정 ({boardDeviceTab === 'pc' ? 'PC' : '모바일'})
        </div>
        <div className="padding-controls">
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>상단 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={extractValueAndUnit(getDevicePadding('Top')).value}
                defaultValue={extractValueAndUnit(getDevicePadding('Top')).value}
                onChange={(e) => {
                  // 입력 중에는 아무 작업도 수행하지 않음
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value;
                  const currentStyle = getDevicePadding('Top');
                  handlePaddingInputChange('Top', inputValue, currentStyle);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(getDevicePadding('Top')).unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>우측 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={extractValueAndUnit(getDevicePadding('Right')).value}
                defaultValue={extractValueAndUnit(getDevicePadding('Right')).value}
                onChange={(e) => {
                  // 입력 중에는 아무 작업도 수행하지 않음
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value;
                  const currentStyle = getDevicePadding('Right');
                  handlePaddingInputChange('Right', inputValue, currentStyle);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(getDevicePadding('Right')).unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>하단 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={extractValueAndUnit(getDevicePadding('Bottom')).value}
                defaultValue={extractValueAndUnit(getDevicePadding('Bottom')).value}
                onChange={(e) => {
                  // 입력 중에는 아무 작업도 수행하지 않음
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value;
                  const currentStyle = getDevicePadding('Bottom');
                  handlePaddingInputChange('Bottom', inputValue, currentStyle);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(getDevicePadding('Bottom')).unit}</span>
            </div>
          </div>
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>좌측 여백</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={extractValueAndUnit(getDevicePadding('Left')).value}
                defaultValue={extractValueAndUnit(getDevicePadding('Left')).value}
                onChange={(e) => {
                  // 입력 중에는 아무 작업도 수행하지 않음
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value;
                  const currentStyle = getDevicePadding('Left');
                  handlePaddingInputChange('Left', inputValue, currentStyle);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(getDevicePadding('Left')).unit}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  
  // 테두리 둥글기 컨트롤 렌더링 함수 추가
  const renderBorderRadiusControl = () => {
    return (
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="6" stroke="currentColor" strokeWidth="2" />
          </svg>
          테두리 둥글기
        </div>
        <div className="padding-controls">
          <div className="padding-control">
            <label style={{ minWidth: '30%' }}>둥글기</label>
            <div className="unit-input-container">
              <input 
                type="text" 
                className="unit-input"
                value={borderRadiusInput}
                onChange={(e) => {
                  setBorderRadiusInput(e.target.value);
                }}
                onBlur={(e) => {
                  handleBorderRadiusChange(e.target.value);
                }}
              />
              <span className="unit-label">{extractValueAndUnit(selectedComponent.style?.borderRadius || '0px').unit}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // 스타일 컨트롤 렌더링 함수 추가
  const renderStyleControls = () => {
    return (
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 7H21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          스타일 설정
        </div>
        <div className="property-controls">
          <div className="property-control">
            <label>배경색</label>
            <div className="color-picker-container">
              <input 
                type="color" 
                value={selectedComponent.style?.backgroundColor || 'transparent'} 
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
              />
            </div>
          </div>
          <div className="property-control">
            <label>테두리 색상</label>
            <div className="color-picker-container">
              <input 
                type="color" 
                value={selectedComponent.style?.borderColor || '#eeeeee'} 
                onChange={(e) => handleStyleChange('borderColor', e.target.value)}
              />
            </div>
          </div>
          <div className="property-control">
            <label>테두리 두께 (px)</label>
            <input
              type="number"
              min="0"
              max="10"
              value={parseInt(selectedComponent.style?.borderWidth) || 1}
              onChange={(e) => handleStyleChange('borderWidth', `${e.target.value}px`)}
            />
          </div>
          <div className="property-control">
            <label>그림자 효과</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="boxShadow"
                checked={!!selectedComponent.style?.boxShadow && selectedComponent.style.boxShadow !== 'none'}
                onChange={(e) => {
                  handleStyleChange(
                    'boxShadow',
                    e.target.checked ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'
                  );
                }}
              />
              <label htmlFor="boxShadow"></label>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
 
  // 포커스 상태 처리 함수
  const handlePropFocus = (property) => {
    setFocusedProps({
      ...focusedProps,
      [property]: true
    });
  };
  
  // 포커스 아웃 시 기본값 처리 함수
  const handlePropBlur = (property, value, defaultValue) => {
    // 포커스 상태 해제
    setFocusedProps({
      ...focusedProps,
      [property]: false
    });
    
    // 값이 비어있는 경우 기본값 설정
    if (value === '' || value === null || value === undefined) {
      // 사용자에게 알림
      alert(`${property} 속성에 기본값을 설정해야 합니다.`);
      // 기본값으로 변경
      handleComponentPropsChange(property, defaultValue);
    }
  };
  
  // 배너 이미지 업로드 핸들러
  const handleBannerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');

      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }
      );

      if (res.data && res.data.file) {
        setBannerEditData({...bannerEditData, icon: res.data.file});
      }
    } catch (err) {
      console.error('이미지 업로드 실패:', err);
      alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
    }
  };
  
  // 배너 비디오 업로드 핸들러
  const handleBannerVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');

      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
        }
      );

      if (res.data && res.data.file) {
        setBannerEditData({...bannerEditData, videoUrl: res.data.file});
      }
    } catch (err) {
      console.error('비디오 업로드 실패:', err);
      alert('비디오 업로드에 실패했습니다. 다시 시도해주세요.');
    }
  };
  
  // 배너 저장 핸들러
  const handleSaveBanner = () => {
    // 미디어 타입에 따라 유효성 검사
    if (bannerEditData.mediaType === 'video') {
      if (!bannerEditData.videoUrl) {
        alert('비디오 URL을 입력해주세요.');
        return;
      }
    } else {
      if (!bannerEditData.icon) {
        alert('배너 이미지를 입력하거나 업로드해주세요.');
        return;
      }
    }
    
    
    // 기존 컴포넌트 속성 복사
    const existingProps = selectedComponent.componentProps ? 
      JSON.parse(JSON.stringify(selectedComponent.componentProps)) : 
      {};
    
    // 디바이스 속성 탭이 선택된 경우
    if (devicePropertyTab) {
      
      // deviceProperty 초기화
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = { pc: {}, mobile: {} };
      }
      
      // 현재 디바이스의 banners 배열 가져오기
      const deviceBanners = existingProps.deviceProperty[devicePropertyTab]?.banners || [];
      let updatedDeviceBanners = [...deviceBanners];
      
      // mediaType 확인 및 설정
      let mediaType = bannerEditData.mediaType;
      if (bannerEditData.videoUrl && !mediaType) {
        mediaType = 'video';
      } else if (!bannerEditData.videoUrl && !mediaType) {
        mediaType = 'image';
      }
      
      if (bannerEditData.index >= 0) {
        // 기존 배너 수정
        updatedDeviceBanners[bannerEditData.index] = {
          ...updatedDeviceBanners[bannerEditData.index],
          text: bannerEditData.title,
          description: bannerEditData.description,
          url: bannerEditData.url,
          icon: bannerEditData.icon,
          mediaType: mediaType,
          videoUrl: bannerEditData.videoUrl || '',
          autoplay: true,  // 항상 true로 설정
          muted: true,     // 항상 true로 설정
          loop: bannerEditData.loop,
          position: bannerEditData.position || '5',
          hasBackground: bannerEditData.hasBackground,
          textColor: bannerEditData.textColor,
          textShadow: bannerEditData.textShadow,
          buttonBgColor: bannerEditData.buttonBgColor,
          buttonTextColor: bannerEditData.buttonTextColor,
          buttonHoverColor: bannerEditData.buttonHoverColor,
          buttonText: bannerEditData.buttonText,
          // 새로 추가된 속성들
          transparentButton: bannerEditData.transparentButton,
          buttonWidth: bannerEditData.buttonWidth,
          buttonHeight: bannerEditData.buttonHeight,
          mobileButtonWidth: bannerEditData.mobileButtonWidth,
          mobileButtonHeight: bannerEditData.mobileButtonHeight
        };
      } else {
        // 새 배너 추가
        updatedDeviceBanners.push({
          text: bannerEditData.title,
          description: bannerEditData.description,
          url: bannerEditData.url,
          icon: bannerEditData.icon,
          mediaType: mediaType,
          videoUrl: bannerEditData.videoUrl || '',
          autoplay: true,  // 항상 true로 설정
          muted: true,     // 항상 true로 설정
          loop: bannerEditData.loop,
          position: bannerEditData.position || '5',
          hasBackground: bannerEditData.hasBackground,
          textColor: bannerEditData.textColor,
          textShadow: bannerEditData.textShadow,
          buttonBgColor: bannerEditData.buttonBgColor,
          buttonTextColor: bannerEditData.buttonTextColor,
          buttonHoverColor: bannerEditData.buttonHoverColor,
          buttonText: bannerEditData.buttonText,
          // 새로 추가된 속성들
          transparentButton: bannerEditData.transparentButton,
          buttonWidth: bannerEditData.buttonWidth,
          buttonHeight: bannerEditData.buttonHeight,
          mobileButtonWidth: bannerEditData.mobileButtonWidth,
          mobileButtonHeight: bannerEditData.mobileButtonHeight
        });
      }
      
      // 디바이스별 배너 업데이트
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      existingProps.deviceProperty[devicePropertyTab].banners = updatedDeviceBanners;
      
      // PC 모드일 경우 최상위 banners 배열도 업데이트
      if (devicePropertyTab === 'pc') {
        existingProps.banners = updatedDeviceBanners;
      }
      
      // 배너 데이터가 변경되었음을 전역 이벤트로 알림
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('bannerDataChanged', {
          detail: {
            deviceType: devicePropertyTab,
            componentId: selectedComponent.id
                          }
                        }));
      }
    } else {
      // 일반 모드 - 최상위 속성과 모든 디바이스 속성에 동일하게 적용
      const currentBanners = existingProps.banners || [];
      let updatedBanners = [...currentBanners];
      
      // mediaType 확인 및 설정
      let mediaType = bannerEditData.mediaType;
      if (bannerEditData.videoUrl && !mediaType) {
        mediaType = 'video';
      } else if (!bannerEditData.videoUrl && !mediaType) {
        mediaType = 'image';
      }
      
      if (bannerEditData.index >= 0) {
        // 기존 배너 수정
        updatedBanners[bannerEditData.index] = {
          ...updatedBanners[bannerEditData.index],
          text: bannerEditData.title,
          description: bannerEditData.description,
          url: bannerEditData.url,
          icon: bannerEditData.icon,
          mediaType: mediaType,
          videoUrl: bannerEditData.videoUrl || '',
          autoplay: true,  // 항상 true로 설정
          muted: true,     // 항상 true로 설정
          loop: bannerEditData.loop,
          position: bannerEditData.position || '5',
          hasBackground: bannerEditData.hasBackground,
          textColor: bannerEditData.textColor,
          textShadow: bannerEditData.textShadow,
          buttonBgColor: bannerEditData.buttonBgColor,
          buttonTextColor: bannerEditData.buttonTextColor,
          buttonHoverColor: bannerEditData.buttonHoverColor,
          buttonText: bannerEditData.buttonText,
          // 새로 추가된 속성들
          transparentButton: bannerEditData.transparentButton,
          buttonWidth: bannerEditData.buttonWidth,
          buttonHeight: bannerEditData.buttonHeight,
          mobileButtonWidth: bannerEditData.mobileButtonWidth,
          mobileButtonHeight: bannerEditData.mobileButtonHeight
        };
      } else {
        // 새 배너 추가
        updatedBanners.push({
          text: bannerEditData.title,
          description: bannerEditData.description,
          url: bannerEditData.url,
          icon: bannerEditData.icon,
          mediaType: mediaType,
          videoUrl: bannerEditData.videoUrl || '',
          autoplay: true,  // 항상 true로 설정
          muted: true,     // 항상 true로 설정
          loop: bannerEditData.loop,
          position: bannerEditData.position || '5',
          hasBackground: bannerEditData.hasBackground,
          textColor: bannerEditData.textColor,
          textShadow: bannerEditData.textShadow,
          buttonBgColor: bannerEditData.buttonBgColor,
          buttonTextColor: bannerEditData.buttonTextColor,
          buttonHoverColor: bannerEditData.buttonHoverColor,
          buttonText: bannerEditData.buttonText,
          // 새로 추가된 속성들
          transparentButton: bannerEditData.transparentButton,
          buttonWidth: bannerEditData.buttonWidth,
          buttonHeight: bannerEditData.buttonHeight,
          mobileButtonWidth: bannerEditData.mobileButtonWidth,
          mobileButtonHeight: bannerEditData.mobileButtonHeight
        });
      }
      
      // 최상위 banners 배열 업데이트
      existingProps.banners = updatedBanners;
      
      // deviceProperty가 있으면 모든 디바이스에도 동일하게 적용
      if (existingProps.deviceProperty) {
        if (!existingProps.deviceProperty.pc) existingProps.deviceProperty.pc = {};
        if (!existingProps.deviceProperty.mobile) existingProps.deviceProperty.mobile = {};
        
        existingProps.deviceProperty.pc.banners = updatedBanners;
        existingProps.deviceProperty.mobile.banners = updatedBanners;
      }
      
      // 배너 데이터가 변경되었음을 전역 이벤트로 알림
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('bannerDataChanged', {
          detail: {
            deviceType: 'all',
            componentId: selectedComponent.id
          }
        }));
      }
    }
    
    
    // 컴포넌트 속성 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
    
    // 편집 모달 닫기
    setShowBannerEdit(false);
  };
  
  // 상태 변수 추가
  // const [isRichEditorOpen, setIsRichEditorOpen] = useState(false);
  // const [richEditorContent, setRichEditorContent] = useState('');

  // 리치 에디터 저장 핸들러
  // const handleRichEditorSave = (content) => {
    
  //   // 디바이스 속성 탭이 선택된 경우
  //   if (devicePropertyTab) {
  //     const existingContent = selectedComponent.content ? 
  //       JSON.parse(JSON.stringify(selectedComponent.content)) : 
  //       { deviceProperty: { pc: '', mobile: '' } };
      
  //     // deviceProperty가 없으면 초기화
  //     if (!existingContent.deviceProperty) {
  //       existingContent.deviceProperty = { pc: '', mobile: '' };
  //     }
      
  //     // 현재 디바이스 탭에 내용 저장 (문자열로 저장)
  //     existingContent.deviceProperty[devicePropertyTab] = content;
      
  //     // PC일 경우 최상위에도 저장
  //     if (devicePropertyTab === 'pc') {
  //       existingContent.html = content;
  //     }
      
      
  //     dispatch(updateComponent({
  //       id: selectedComponent.id,
  //       changes: {
  //         content: existingContent
  //       }
  //     }));
  //   } else {
  //     // 일반 모드일 경우 직접 content에 저장
  //     dispatch(updateComponent({
  //       id: selectedComponent.id,
  //       changes: {
  //         content: content
  //       }
  //     }));
  //   }
    
  //   // 모달 닫기
  //   setIsRichEditorOpen(false);
  // };
  const handleRichEditorSave = (content) => {
    // content가 문자열인지 확인
    if (typeof content !== 'string') {
      console.error('저장 실패: 콘텐츠가 문자열이 아닙니다', content);
      return;
    }
    
    // RICH_EDITOR 컴포넌트일 경우 간단히 처리
    if (selectedComponent.type === COMPONENT_TYPES.RICH_EDITOR) {
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    } 
    // 다른 컴포넌트(이미지-텍스트 등)는 기존 방식대로 처리
    else if (devicePropertyTab) {
      const existingContent = selectedComponent.content ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        { deviceProperty: { pc: '', mobile: '' } };
      
      // deviceProperty가 없으면 초기화
      if (!existingContent.deviceProperty) {
        existingContent.deviceProperty = { pc: '', mobile: '' };
      }
      
      // 현재 디바이스 탭에 내용 저장 (문자열로 저장)
      existingContent.deviceProperty[devicePropertyTab] = content;
      
      // PC일 경우 최상위에도 저장
      if (devicePropertyTab === 'pc') {
        existingContent.html = content;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: existingContent
        }
      }));
    } else {
      // 일반 모드일 경우 직접 content에 저장
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    }
    
    // 모달 닫기
    setIsRichEditorOpen(false);
  };




  // 리치 에디터 모달 렌더링 부분 (파일 끝부분에 추가)
  // ...

  // 위치 라벨 반환 함수 추가 (파일 끝 부분에 추가)
  const getPosLabel = (pos) => {
    switch(pos) {
      case '1': return '좌상';
      case '2': return '중상';
      case '3': return '우상';
      case '4': return '좌중';
      case '5': return '중앙';
      case '6': return '우중';
      case '7': return '좌하';
      case '8': return '중하';
      case '9': return '우하';
      default: return '';
    }
  };

  return (
    <div className="property-panel">
      {renderTabs()}
      
      {activeTab === 'layout' ? (
        <div className="layout-options">
          <LayoutSelector />
        </div>
      ) : (
        <div className="component-properties">
          <h3>컴포넌트 속성</h3>
          {renderDevicePropertyTabs()}
          {renderProperties()}
          <button 
            className="delete-button" 
            onClick={handleDelete}
          >
            컴포넌트 삭제
                    </button>
                </div>
      )}
      
      {/* 리치 에디터 모달 */}
      <RichEditorModal 
        isOpen={isRichEditorOpen}
        onClose={() => setIsRichEditorOpen(false)}
        initialContent={richEditorContent}
        onSave={handleRichEditorSave}
      />
    </div>
  );
}

export { PropertyPanel };
export default PropertyPanel;